/**
 * CartController - Simplified, unified cart management
 * 
 * Replaces the fragmented cart system with ONE controller that handles:
 * - Add to cart (from PDP, recommendations, quick add, anywhere)
 * - Quantity updates (bulk-friendly, debounced)
 * - Tiered discounts (calculated inline, no race conditions)
 * - UI updates (targeted, no full HTML replacement)
 * 
 * Usage:
 *   CartController.add(variantId, quantity)
 *   CartController.updateQuantity(lineIndex, newQuantity)
 *   CartController.remove(lineIndex)
 */

(function () {
if (window.CartController?.__dtScriptLoaded) {
    window.dispatchEvent(new CustomEvent('theme:cart-ready'));
    return;
}

var CART_VALUE_OFFERS = window.CART_VALUE_OFFERS || [
    { threshold: 3000, discountLabel: '5% OFF', shortLabel: '5%', title: 'Get 5% off', offerKey: 'dt5' },
    { threshold: 5000, discountLabel: '10% OFF', shortLabel: '10%', title: 'Get 10% off', offerKey: 'dt10' },
    { threshold: 10000, discountLabel: '15% OFF', shortLabel: '15%', title: 'Get 15% off', offerKey: 'dt15' },
    { threshold: 20000, discountLabel: '20% OFF', shortLabel: '20%', title: 'Get 20% off', offerKey: 'dt20' }
];
window.CART_VALUE_OFFERS = CART_VALUE_OFFERS;

const CART_SAVINGS_CARD_SELECTOR = '[data-cart-savings-progress]';
const CART_SAVINGS_SENTINEL_SELECTOR = '[data-cart-savings-sticky-sentinel]';
const CART_SAVINGS_SPACE_SELECTOR = '[data-cart-savings-space]';

function getCartSavingsProgressSpace(card) {
    let sibling = card?.nextElementSibling || null;
    while (sibling && !sibling.matches?.(CART_SAVINGS_CARD_SELECTOR)) {
        if (sibling.matches?.(CART_SAVINGS_SPACE_SELECTOR)) return sibling;
        sibling = sibling.nextElementSibling;
    }
    return null;
}

function refreshCartSavingsProgressWhenDrawerIsReady() {
    if (!window.CartSavingsProgressCollapse || typeof window.CartSavingsProgressCollapse.refresh !== 'function') return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer?.classList.contains('is-opening') && typeof drawer.scheduleAfterOpening === 'function') {
        drawer.scheduleAfterOpening(() => window.CartSavingsProgressCollapse.refresh());
        return;
    }

    window.CartSavingsProgressCollapse.refresh();
}

function initializeCartSavingsProgressCollapse() {
    if (window.CartSavingsProgressCollapse) {
        refreshCartSavingsProgressWhenDrawerIsReady();
        return;
    }

    const SCROLL_PARENT_SELECTOR = '.cart_overflow';
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const easeInOut = (value) => value * value * (3 - (2 * value));
    let rafId = 0;
    let measureRafId = 0;
    let measureTimerId = 0;
    let observedScrollParents = new WeakSet();

    const getScrollParent = (card) => card.closest(SCROLL_PARENT_SELECTOR) || window;
    const getScrollTop = (scrollParent) => scrollParent === window
        ? window.scrollY || window.pageYOffset || 0
        : scrollParent.scrollTop || 0;
    const isCollapsibleCard = (card) => Boolean(
        card.closest('.cart-page-drawer-style') || card.parentElement?.classList.contains('cart_overflow_inner')
    );
    const getSpace = getCartSavingsProgressSpace;

    const updateCard = (card) => {
        if (card.hidden || !isCollapsibleCard(card) || !card.getClientRects().length) return;

        const scrollParent = getScrollParent(card);
        const scrollTop = getScrollTop(scrollParent);
        const startOffset = parseFloat(card.dataset.cartSavingsStartOffset || '0');
        const collapseRange = parseFloat(card.dataset.cartSavingsCollapseRange || '180');
        const fullHeight = parseFloat(card.style.getPropertyValue('--cart-savings-full-height') || '0');
        const compactHeight = parseFloat(card.style.getPropertyValue('--cart-savings-compact-height') || '0');
        const sentinel = card.previousElementSibling?.matches?.(CART_SAVINGS_SENTINEL_SELECTOR)
            ? card.previousElementSibling
            : null;
        const scrollParentRect = scrollParent === window
            ? { top: 0, left: 0, width: window.innerWidth || document.documentElement.clientWidth || 0 }
            : scrollParent.getBoundingClientRect();
        const scrollParentTop = scrollParentRect.top;
        const fullBottom = sentinel
            ? sentinel.getBoundingClientRect().top + fullHeight
            : startOffset + fullHeight - scrollTop;
        const isCollapsed = fullHeight && compactHeight
            ? fullBottom <= scrollParentTop
            : clamp((scrollTop - startOffset) / collapseRange, 0, 1) > 0.98;
        const updateKey = isCollapsed ? 'collapsed' : 'full';
        if (card.dataset.cartSavingsCollapseKey === updateKey) return;

        const space = getSpace(card);
        const shellHeight = fullHeight;
        const fullOpacity = isCollapsed ? 0 : 1;
        const compactOpacity = isCollapsed ? 1 : 0;
        const borderOpacity = isCollapsed ? 0 : 0.08;
        const shadowOpacity = isCollapsed ? 0.08 : 0;

        card.dataset.cartSavingsCollapseKey = updateKey;
        if (shellHeight) card.style.setProperty('--cart-savings-shell-height', `${Math.ceil(shellHeight)}px`);
        if (isCollapsed) {
            card.style.setProperty('--cart-savings-fixed-top', `${Math.round(scrollParentRect.top)}px`);
            card.style.setProperty('--cart-savings-fixed-left', `${Math.round(scrollParentRect.left)}px`);
            card.style.setProperty('--cart-savings-fixed-width', `${Math.round(scrollParentRect.width)}px`);
        }
        card.style.setProperty('--cart-savings-full-opacity', fullOpacity.toFixed(3));
        card.style.setProperty('--cart-savings-compact-opacity', compactOpacity.toFixed(3));
        card.style.setProperty('--cart-savings-full-shift-y', '0px');
        card.style.setProperty('--cart-savings-compact-shift-y', '0px');
        card.style.setProperty('--cart-savings-border-opacity', borderOpacity.toFixed(3));
        card.style.setProperty('--cart-savings-shadow', `0 1px 0 rgba(0, 0, 0, ${shadowOpacity.toFixed(3)})`);
        card.classList.toggle('is-cart-savings-collapsed', isCollapsed);
        if (space) space.style.height = '0px';
    };

    const measureCard = (card) => {
        // Keep the last valid sizes while the cart is hidden behind Orders.
        if (!card.getClientRects().length) return;

        if (card.hidden || !isCollapsibleCard(card)) {
            const space = getSpace(card);
            if (space) {
                space.style.setProperty('--cart-savings-reserved-space', '0px');
                space.style.height = '0px';
            }
            card.classList.remove('is-cart-savings-ready', 'is-cart-savings-collapsed');
            return;
        }

        const compact = card.querySelector('[data-cart-savings-compact]');
        const full = card.querySelector('[data-cart-savings-full]');
        const sentinel = card.previousElementSibling?.matches?.(CART_SAVINGS_SENTINEL_SELECTOR)
            ? card.previousElementSibling
            : null;
        const space = getSpace(card);
        if (!compact || !full || !sentinel || !space) return;

        const wasReady = card.classList.contains('is-cart-savings-ready');
        let fullGap = parseFloat(card.dataset.cartSavingsFullGap || 'NaN');
        if (!wasReady || Number.isNaN(fullGap)) {
            fullGap = parseFloat(window.getComputedStyle(card).marginBottom) || 0;
            card.dataset.cartSavingsFullGap = String(fullGap);
        }

        const fullHeight = Math.ceil(wasReady ? full.offsetHeight : card.offsetHeight);
        const compactHeight = Math.ceil(compact.offsetHeight || compact.scrollHeight);
        const reserveHeight = Math.max(0, fullHeight + fullGap - compactHeight);
        const scrollParent = getScrollParent(card);
        const scrollTop = getScrollTop(scrollParent);
        const stickyTop = parseFloat(window.getComputedStyle(card).top) || 0;
        const startOffset = scrollParent === window
            ? sentinel.getBoundingClientRect().top + scrollTop - stickyTop
            : sentinel.offsetTop - stickyTop;
        const collapseRange = Math.max(160, Math.min(260, (reserveHeight * 1.45) || 180));

        card.style.setProperty('--cart-savings-full-height', `${fullHeight}px`);
        card.style.setProperty('--cart-savings-compact-height', `${compactHeight}px`);
        card.style.setProperty('--cart-savings-shell-height', `${fullHeight}px`);
        space.style.setProperty('--cart-savings-reserved-space', `${reserveHeight}px`);
        space.style.height = '0px';
        card.dataset.cartSavingsStartOffset = String(startOffset);
        card.dataset.cartSavingsCollapseRange = String(collapseRange);
        card.dataset.cartSavingsCollapseKey = '';
        card.classList.add('is-cart-savings-ready');
        updateCard(card);
    };

    const updateAll = () => {
        rafId = 0;
        document.querySelectorAll(CART_SAVINGS_CARD_SELECTOR).forEach(updateCard);
    };

    const scheduleUpdate = () => {
        if (rafId) return;
        rafId = window.requestAnimationFrame(updateAll);
    };

    const observeScrollParents = () => {
        document.querySelectorAll(CART_SAVINGS_CARD_SELECTOR).forEach((card) => {
            if (!isCollapsibleCard(card)) return;
            const scrollParent = getScrollParent(card);
            if (observedScrollParents.has(scrollParent)) return;
            observedScrollParents.add(scrollParent);
            scrollParent.addEventListener('scroll', scheduleUpdate, { passive: true });
        });
    };

    const measureAll = () => {
        measureRafId = 0;
        observeScrollParents();
        document.querySelectorAll(CART_SAVINGS_CARD_SELECTOR).forEach(measureCard);
    };

    const refresh = (options = {}) => {
        const delay = Number(options.delay || 0);
        if (delay > 0) {
            if (measureTimerId) clearTimeout(measureTimerId);
            measureTimerId = window.setTimeout(() => {
                measureTimerId = 0;
                refresh();
            }, delay);
            return;
        }
        if (measureRafId) return;
        measureRafId = window.requestAnimationFrame(measureAll);
    };

    window.CartSavingsProgressCollapse = { refresh };
    window.addEventListener('resize', refresh, { passive: true });
    document.addEventListener('cart:updated', refresh);

    if ('MutationObserver' in window && document.body) {
        const observerTarget = document.querySelector('cart-drawer') || document.body;
        new MutationObserver(() => refresh({ delay: 80 })).observe(observerTarget, { childList: true, subtree: true });
    }

    refresh();
}

function getCartSavingsProgressBlockHtml(card) {
    if (!card) return '';

    const sentinel = card.previousElementSibling?.matches?.(CART_SAVINGS_SENTINEL_SELECTOR)
        ? card.previousElementSibling
        : null;
    const space = getCartSavingsProgressSpace(card);
    return `${sentinel ? sentinel.outerHTML : ''}${card.outerHTML}${space ? space.outerHTML : ''}`;
}

function removeCartSavingsProgressBlock(card) {
    if (!card) return;

    const sentinel = card.previousElementSibling?.matches?.(CART_SAVINGS_SENTINEL_SELECTOR)
        ? card.previousElementSibling
        : null;
    const space = getCartSavingsProgressSpace(card);

    if (sentinel) sentinel.remove();
    if (space) space.remove();
    card.remove();
}

function replaceCartSavingsProgressBlock(container, newCard) {
    if (!container) return;

    const currentCard = container.querySelector(CART_SAVINGS_CARD_SELECTOR);
    if (currentCard) {
        if (window.CartSavingsProgressCollapse && typeof window.CartSavingsProgressCollapse.refresh === 'function') {
            refreshCartSavingsProgressWhenDrawerIsReady();
        }
        return;
    }

    const newHtml = getCartSavingsProgressBlockHtml(newCard);
    container.querySelectorAll(CART_SAVINGS_CARD_SELECTOR).forEach(removeCartSavingsProgressBlock);
    container.querySelectorAll(`${CART_SAVINGS_SENTINEL_SELECTOR}, ${CART_SAVINGS_SPACE_SELECTOR}`).forEach((element) => {
        element.remove();
    });

    if (newHtml) {
        container.insertAdjacentHTML('afterbegin', newHtml);
    }

    if (window.CartSavingsProgressCollapse && typeof window.CartSavingsProgressCollapse.refresh === 'function') {
        refreshCartSavingsProgressWhenDrawerIsReady();
    }
}

function removeCartDrawerMerchandising() {
    document.querySelectorAll([
        '.cart_overflow_inner .cart-savings-progress',
        '.cart_overflow_inner .cart-savings-progress__sticky-sentinel',
        '.cart_overflow_inner .cart-savings-progress__space',
        '.cart_overflow_inner .ship_detail',
        '.cart_overflow_inner .cart-drawer__price-breakdown',
        '.cart_overflow_inner .offers-section',
        '.cart_overflow_inner .cart-drawer__customers-bought',
        '.cart_overflow_inner .cart-drawer__policy-section',
        '.cart_overflow_inner .cart-page__wishlist-panel',
        '.cart_overflow_inner .cart-drawer-deals-banner'
    ].join(',')).forEach((element) => element.remove());

    document.querySelectorAll('.drawer__footer').forEach((footer) => {
        footer.hidden = true;
    });
}

initializeCartSavingsProgressCollapse();

function isEasyBundlePage() {
    return window.location.pathname.indexOf('/apps/gbb/easybundle/') === 0;
}

const DT_EASYBUNDLE_CART_META_STORAGE_KEY = 'dtEasyBundleCartMeta';
const DT_EASYBUNDLE_INSTANCE_META_STORAGE_KEY = 'dtEasyBundleInstanceMeta';
const DT_EASYBUNDLE_DETAILS_DURATION = 220;
const DT_CURATED_EASYBUNDLE_CART_META = {
    title: 'Buy 2 and get 10% Off',
    image: 'https://d3ks0ngva6go34.cloudfront.net/giftBox/drtowel.myshopify.com/1781865380336.png',
    url: '/apps/gbb/easybundle/curated-collection?pb=0'
};

function getActiveEasyBundleData() {
    return window.easybundle_active_bundle_data?._bundleData ||
        window.easybundle_full_page_ext_data?._bundleData ||
        window.easybundles_preResolvedSettings?._bundleData ||
        window.easybundle_active_bundle_data ||
        null;
}

function getStoredEasyBundleCartMeta() {
    try {
        const meta = JSON.parse(window.localStorage.getItem(DT_EASYBUNDLE_CART_META_STORAGE_KEY) || '{}');
        if (!meta || typeof meta !== 'object') return null;
        if (!meta.title && !meta.image && !meta.url) return null;
        return meta;
    } catch (error) {
        return null;
    }
}

function getStoredEasyBundleInstanceMeta() {
    try {
        const meta = JSON.parse(window.localStorage.getItem(DT_EASYBUNDLE_INSTANCE_META_STORAGE_KEY) || '{}');
        return meta && typeof meta === 'object' ? meta : {};
    } catch (error) {
        return {};
    }
}

function setStoredEasyBundleInstanceMeta(instanceMeta) {
    try {
        window.localStorage.setItem(DT_EASYBUNDLE_INSTANCE_META_STORAGE_KEY, JSON.stringify(instanceMeta || {}));
    } catch (error) {
        // localStorage can be unavailable; the server-rendered cart still works.
    }
}

function getEasyBundleInstanceFromRow(row) {
    const code = row?.dataset?.easybundleCode || '';
    return code.indexOf('EasyBundleInstance-') === 0 ? code.replace('EasyBundleInstance-', '') : '';
}

function getEasyBundleCartLineMeta(row) {
    const code = row?.dataset?.easybundleCode || '';
    const items = CartController?.cart?.items || [];
    if (!code || !Array.isArray(items)) return null;

    const item = items.find((cartItem) => CartController.getBundleGroupKey(cartItem) === code);
    if (!item) return null;

    const offerId = CartController.getItemProperty(item, '_easyBundle:OfferId') ||
        CartController.getItemProperty(item, '_bundleOfferId') ||
        CartController.getItemProperty(item, '_originalOfferId');
    return getEasyBundleOfferMetaById(offerId);
}

function getCurrentEasyBundleUrl() {
    if (window.location.pathname.indexOf('/apps/gbb/easybundle/') === 0) {
        return `${window.location.pathname}${window.location.search || ''}`;
    }
    if (getActiveEasyBundleData()) {
        return `${window.location.pathname}${window.location.search || ''}`;
    }
    return '';
}

function getEasyBundleOfferMeta(source = '') {
    const text = String(source || '').replace(/,/g, '');
    const banners = window.__dtEasyBundleOfferBanners || {};
    const routes = [
        ['/apps/gbb/easybundle/buy-3-at-1499', '1499'],
        ['/apps/gbb/easybundle/buy-3-at-2499', '2499'],
        ['/apps/gbb/easybundle/buy-3-at-3499', '3499'],
        ['/apps/gbb/easybundle/buy-3-at-5499', '5499']
    ];
    const found = routes.find(([path, price]) => text.includes(path) || text.includes(price));
    if (!found) return null;

    const [path] = found;
    const banner = banners[path];
    if (!banner?.src) return null;
    return {
        image: banner.src,
        alt: banner.alt,
        url: path
    };
}

function getEasyBundleOfferMetaById(offerId = '') {
    const normalized = String(offerId || '').split('#')[0].split('_')[0];
    const offers = {
        'SDK-730282': { title: 'Buy 3 at ₹1499', url: '/apps/gbb/easybundle/buy-3-at-1499' },
        'SDK-302967': { title: 'Buy 3 at ₹1499', url: '/apps/gbb/easybundle/buy-3-at-1499' },
        'SDK-032354': { title: 'Buy 3 at ₹2499', url: '/apps/gbb/easybundle/buy-3-at-2499' },
        'SDK-422483': { title: 'Buy 3 at ₹2499', url: '/apps/gbb/easybundle/buy-3-at-2499' },
        'SDK-732230': { title: 'Buy 3 at ₹3499', url: '/apps/gbb/easybundle/buy-3-at-3499' },
        'SDK-581440': { title: 'Buy 3 at ₹3499', url: '/apps/gbb/easybundle/buy-3-at-3499' },
        'SDK-017228': { title: 'Buy 3 at ₹5499', url: '/apps/gbb/easybundle/buy-3-at-5499' },
        'SDK-275384': { title: 'Buy 3 at ₹5499', url: '/apps/gbb/easybundle/buy-3-at-5499' }
    };
    const meta = offers[normalized];
    if (!meta) return null;
    const offerMeta = getEasyBundleOfferMeta(meta.url);
    return {
        title: meta.title,
        url: meta.url,
        image: offerMeta?.image || '',
        imageAlt: offerMeta?.alt || `${meta.title} Doctor Towels bundle offer`
    };
}

function getEasyBundleCartMeta() {
    const data = getActiveEasyBundleData();
    const banners = data?.bundleSettings?.bundleBanners || {};
    const parentProductImage = data?.parentProductShopifyData?.image?.src ||
        data?.parentProductShopifyData?.images?.[0]?.src ||
        '';
    const image = banners.mobileBanner || banners.desktopBanner || parentProductImage;
    const currentBundleUrl = getCurrentEasyBundleUrl();
    const meta = {
        title: data?.bundleName || data?.parentProductShopifyData?.title || '',
        image,
        url: currentBundleUrl || data?.bundleLink || ''
    };
    const offerMeta = getEasyBundleOfferMeta([meta.title, meta.url, window.location.pathname].join(' '));
    if (offerMeta) {
        meta.image = offerMeta.image;
        meta.imageAlt = offerMeta.alt;
        if (!meta.url) meta.url = offerMeta.url;
    }

    if ((!meta.title && !meta.image) && window.location.pathname.indexOf('/apps/gbb/easybundle/curated-collection') === 0) {
        meta.title = DT_CURATED_EASYBUNDLE_CART_META.title;
        meta.image = DT_CURATED_EASYBUNDLE_CART_META.image;
    }
    if (!meta.url && window.location.pathname.indexOf('/apps/gbb/easybundle/curated-collection') === 0) {
        meta.url = DT_CURATED_EASYBUNDLE_CART_META.url;
    }

    if (meta.title || meta.image || meta.url) {
        try {
            window.localStorage.setItem(DT_EASYBUNDLE_CART_META_STORAGE_KEY, JSON.stringify(meta));
        } catch (error) {
            // localStorage can be unavailable in private browsing; the visible row still updates.
        }
        return meta;
    }

    return getStoredEasyBundleCartMeta();
}

function normalizeEasyBundleItemText(value) {
    return String(value || '').toLowerCase().replace(/^\s*\d+\s*x\s*/, '').replace(/terracotta/g, 'teracotta').replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
}

function applyEasyBundleItemThumbnails(root = document) {
    const thumbnails = root.querySelectorAll('[data-dt-bundle-item-thumbnail]');
    if (!thumbnails.length) return;
    const selectedProducts = Array.isArray(window.__dtEasyBundleCartItems)
        ? window.__dtEasyBundleCartItems.map((item) => ({
            image: { currentSrc: item.image || '', src: item.image || '' },
            text: normalizeEasyBundleItemText(`${item.title || ''} ${item.variant || item.variantTitle || ''}`)
        })).filter((item) => item.image.currentSrc)
        : [];
    const catalogProducts = Array.from(document.querySelectorAll('.gbbProductItem')).map((card) => {
        const image = card.querySelector('.gbbProductImageContainer img, img');
        return image?.currentSrc || image?.src ? { image, text: normalizeEasyBundleItemText(card.textContent) } : null;
    }).filter(Boolean);
    const products = selectedProducts.concat(catalogProducts);
    thumbnails.forEach((thumbnail) => {
        const itemName = normalizeEasyBundleItemText(thumbnail.dataset.dtBundleItemName);
        const product = products.find((entry) => itemName && itemName.split(' ').every((token) => token.length < 2 || entry.text.includes(token)));
        if (!product) return;
        thumbnail.src = product.image.currentSrc || product.image.src;
        thumbnail.alt = thumbnail.dataset.dtBundleItemName.replace(/^\s*\d+\s*x\s*/, '');
    });
}

function initializeEasyBundleItemThumbnailUpdates() {
    if (document.documentElement.dataset.dtEasyBundleThumbnailUpdates === 'true') return;
    document.documentElement.dataset.dtEasyBundleThumbnailUpdates = 'true';
    document.addEventListener('toggle', (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.matches('.dt-cart-easybundle')) return;
        applyEasyBundleItemThumbnails(details);
    }, true);
}

initializeEasyBundleItemThumbnailUpdates();

function applyEasyBundleCartRowMeta() {
    const rows = document.querySelectorAll('.cart-item--easybundle-parent');
    if (!rows.length) {
        initializeEasyBundleDetailsMotion();
        return;
    }

    const instanceMeta = getStoredEasyBundleInstanceMeta();
    const currentMeta = isEasyBundlePage() ? getEasyBundleCartMeta() : null;
    let didUpdateStorage = false;

    rows.forEach((row) => {
        const instance = getEasyBundleInstanceFromRow(row);
        if (!instance) return;

        const lineMeta = getEasyBundleCartLineMeta(row);
        if (lineMeta && (
            !instanceMeta[instance] ||
            instanceMeta[instance].title !== lineMeta.title ||
            instanceMeta[instance].url !== lineMeta.url
        )) {
            instanceMeta[instance] = lineMeta;
            didUpdateStorage = true;
        } else if (!instanceMeta[instance] && currentMeta) {
            instanceMeta[instance] = currentMeta;
            didUpdateStorage = true;
        }

        const meta = lineMeta || instanceMeta[instance];
        if (!meta) return;

        if (meta.title) {
            const title = row.querySelector('.cart-item__name');
            if (title) title.textContent = meta.title;
        }

        if (meta.url) {
            row.querySelectorAll('.cart-item__name, .cart-item__link').forEach((link) => {
                link.setAttribute('href', meta.url);
            });
        }

        if (meta.image) {
            const image = row.querySelector('.cart-item__image');
            if (image) {
                image.src = meta.image;
                image.alt = meta.imageAlt || (meta.title ? `${meta.title} offer banner` : 'Bundle offer banner');
                image.classList.add('cart-item__image--easybundle-offer');
            }
        }
    });

    if (didUpdateStorage) setStoredEasyBundleInstanceMeta(instanceMeta);
    applyEasyBundleItemThumbnails();
    initializeEasyBundleDetailsMotion();
}

function initializeEasyBundleDetailsMotion(root = document) {
    root.querySelectorAll('.cart-item--easybundle-parent .dt-cart-easybundle').forEach((details) => {
        const summary = details.querySelector('summary');
        if (!summary) return;
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });
}

document.addEventListener('click', (event) => {
    const summary = event.target.closest('.cart-item--easybundle-parent .dt-cart-easybundle > summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!details) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    window.clearTimeout(details.__dtEasyBundleMotionTimer);

    if (details.open && !details.classList.contains('is-opening')) {
        summary.setAttribute('aria-expanded', 'false');
        if (reduceMotion) {
            details.removeAttribute('open');
            details.classList.remove('is-closing', 'is-opening');
            return;
        }

        details.classList.remove('is-opening');
        details.classList.add('is-closing');
        details.__dtEasyBundleMotionTimer = window.setTimeout(() => {
            details.removeAttribute('open');
            details.classList.remove('is-closing');
        }, DT_EASYBUNDLE_DETAILS_DURATION);
        return;
    }

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    details.classList.remove('is-closing');
    if (reduceMotion) {
        details.classList.remove('is-opening');
        return;
    }

    details.classList.add('is-opening');
    window.requestAnimationFrame(() => {
        details.classList.remove('is-opening');
    });
});

function getCartFormProperties(form) {
    const properties = {};
    if (!form || typeof FormData === 'undefined') return properties;

    try {
        const formData = new FormData(form);
        formData.forEach((value, key) => {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch && value !== '') {
                properties[propertyMatch[1]] = value;
            }
        });
    } catch (error) {
        return properties;
    }

    return properties;
}

function getHairNudgeSheetFromTrigger(trigger) {
    if (!trigger) return null;

    const sheetId = trigger.getAttribute('aria-controls');
    if (sheetId) {
        const sheet = document.getElementById(sheetId);
        if (sheet?.matches('[data-cart-hair-nudge-sheet]')) return sheet;
    }

    const nudge = trigger.closest('.cart-hair-nudge');
    return nudge?.nextElementSibling?.matches?.('[data-cart-hair-nudge-sheet]')
        ? nudge.nextElementSibling
        : null;
}

function openHairNudgeSheet(trigger) {
    const sheet = getHairNudgeSheetFromTrigger(trigger);
    if (!sheet) return;

    sheet.__previousFocus = document.activeElement;
    sheet.classList.add('is-open');
    sheet.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-hair-nudge-sheet-open');

    window.requestAnimationFrame(() => {
        sheet.querySelector('.cart-hair-nudge-sheet__panel')?.focus({ preventScroll: true });
    });
}

function closeHairNudgeSheet(sheet) {
    const activeSheet = sheet?.matches?.('[data-cart-hair-nudge-sheet]')
        ? sheet
        : document.querySelector('.cart-hair-nudge-sheet.is-open');

    if (!activeSheet) return;

    activeSheet.classList.remove('is-open');
    activeSheet.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.cart-hair-nudge-sheet.is-open')) {
        document.body.classList.remove('cart-hair-nudge-sheet-open');
    }

    if (activeSheet.__previousFocus && typeof activeSheet.__previousFocus.focus === 'function') {
        window.setTimeout(() => activeSheet.__previousFocus.focus({ preventScroll: true }), 180);
    }
}

function setCartPolicySheetState(root, isOpen) {
    const sheet = root?.querySelector?.('[data-cart-policy-sheet]');
    const trigger = root?.querySelector?.('[data-cart-policy-open]');
    if (!sheet || !trigger) return;

    sheet.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
}

function updateHairNudgeSheetHero(sheet, imageUrl, imageAlt) {
    const heroImage = sheet?.querySelector('[data-cart-hair-sheet-hero-image]');
    if (!heroImage || !imageUrl) return;

    heroImage.src = imageUrl;
    if (imageAlt) heroImage.alt = imageAlt;
}

const CartController = {
    // ============================================
    // STATE
    // ============================================
    cart: null,
    isProcessing: false,
    pendingAddQueue: [],
    pendingAddTimer: null,
    pendingAddPromise: null,
    pendingQuantityUpdates: new Map(), // itemKey -> { lineIndex, quantity }
    pendingQuantityPromise: null,
    pendingRemovalKeys: new Set(),
    pendingRemovalTimer: null,
    pendingRemovalPromise: null,
    debounceTimer: null,
    cartUpdateUnsubscriber: null,
    deferredDrawerRefreshTimer: null,
    mutationVersion: 0,
    DEBOUNCE_MS: 350, // Wait 350ms before syncing (allows rapid clicks)



    // ============================================
    // INITIALIZATION
    // ============================================
    async init() {
        try {
            const response = await fetch('/cart.js');
            if (response.ok) {
                this.cart = await response.json();
                console.log('[CartController] Initialized with', this.cart.item_count, 'items');
            }
        } catch (error) {
            console.error('[CartController] Init failed:', error);
        }

        this.updateSavingsProgressUI();
        this.updateOffersUI();
        this.dedupeCompanionRecommendations();
        this.normalizeDrawerSectionOrder();

        // Setup global event listeners
        this.setupEventListeners();
    },


    setupEventListeners() {
        // Fallback add-to-cart handler for forms that bypass <product-form>
        // (e.g. dynamically rendered home/collection cards)
        document.addEventListener('submit', async (e) => {
            const form = e.target.closest('form');
            if (!form || e.defaultPrevented) return;

            const isAddToCartForm =
                form.matches('[data-type="add-to-cart-form"]') ||
                (form.getAttribute('action') || '').includes('/cart/add');
            if (!isAddToCartForm) return;

            const variantInput = form.querySelector('[name="id"]:checked') || form.querySelector('[name="id"]');
            const selectedVariant = this.getSelectedVariantFromForm(form);
            const selectedVariantId = parseInt(selectedVariant?.id, 10);
            const quantityInput = form.querySelector('[name="quantity"]');
            const variantId = selectedVariantId || parseInt(variantInput?.value, 10);
            const quantity = parseInt(quantityInput?.value, 10) || 1;

            if (!variantId) return;
            if (selectedVariantId && variantInput && variantInput.value !== String(selectedVariantId)) {
                variantInput.value = String(selectedVariantId);
            }

            e.preventDefault();
            e.stopPropagation();

            const submitButton = e.submitter || form.querySelector('[type="submit"][name="add"]');
            const spinner = submitButton?.querySelector('.loading__spinner');
            const isDrawerAdd = Boolean(form.closest('cart-drawer, #CartDrawer, cart-drawer-items'));
            const isHairNudgeSheetAdd = Boolean(form.closest('[data-cart-hair-nudge-sheet]'));
            const shouldShowButtonLoading = !isDrawerAdd || isHairNudgeSheetAdd;

            if (submitButton && submitButton.dataset.cartPending === 'true') {
                return;
            }

            if (submitButton) {
                submitButton.dataset.cartPending = 'true';
            }
            if (submitButton && shouldShowButtonLoading) {
                submitButton.setAttribute('aria-disabled', 'true');
                submitButton.classList.add('loading');
            }
            if (spinner && shouldShowButtonLoading) spinner.classList.remove('hidden');

            const refreshRoutineUpsells = Boolean(form.closest('.cart-face-towels'));
            const optimisticItem = this.getOptimisticItemFromForm(form, variantId, quantity);
            const success = await this.add(variantId, quantity, getCartFormProperties(form), { refreshRoutineUpsells, optimisticItem, sourceForm: form });
            if (!success) {
                const errorContainer = form.querySelector('.product-form__error-message');
                const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
                const hasVisibleError = errorContainer?.textContent?.trim() && errorWrapper && !errorWrapper.hasAttribute('hidden');
                if (!hasVisibleError && errorContainer && errorWrapper) {
                    errorContainer.textContent = window.cartStrings?.error || 'Could not add to cart';
                    errorWrapper.removeAttribute('hidden');
                } else if (!hasVisibleError) {
                    this.showProductFormError(form, window.cartStrings?.error || 'Could not add to cart');
                }
            } else {
                closeHairNudgeSheet(form.closest('[data-cart-hair-nudge-sheet]'));
            }

            if (submitButton) {
                delete submitButton.dataset.cartPending;
                submitButton.classList.remove('loading');
                submitButton.removeAttribute('aria-disabled');
            }
            if (spinner) spinner.classList.add('hidden');
        });

        document.addEventListener('click', (e) => {
            const openButton = e.target.closest('[data-cart-hair-nudge-open]');
            if (openButton) {
                e.preventDefault();
                openHairNudgeSheet(openButton);
                return;
            }

            const closeButton = e.target.closest('[data-cart-hair-nudge-close]');
            if (closeButton) {
                e.preventDefault();
                closeHairNudgeSheet(closeButton.closest('[data-cart-hair-nudge-sheet]'));
                return;
            }

            const policyOpenButton = e.target.closest('[data-cart-policy-open]');
            if (policyOpenButton) {
                e.preventDefault();
                setCartPolicySheetState(policyOpenButton.closest('.cart-drawer__policy-stack'), true);
                return;
            }

            const policyCloseButton = e.target.closest('[data-cart-policy-close]');
            if (policyCloseButton) {
                e.preventDefault();
                setCartPolicySheetState(policyCloseButton.closest('.cart-drawer__policy-stack'), false);
                return;
            }

            const thumbnail = e.target.closest('[data-cart-hair-sheet-thumb]');
            if (thumbnail) {
                const sheet = thumbnail.closest('[data-cart-hair-nudge-sheet]');
                e.preventDefault();
                updateHairNudgeSheetHero(sheet, thumbnail.dataset.imageUrl, thumbnail.dataset.imageAlt);
                sheet?.querySelectorAll('[data-cart-hair-sheet-thumb]').forEach((button) => {
                    button.classList.toggle('is-active', button === thumbnail);
                });
            }
        });

        document.addEventListener('change', (e) => {
            const input = e.target.closest('.cart-hair-nudge__color input[name="id"]');
            if (!input || !input.checked || !input.dataset.imageUrl) return;

            const nudge = input.closest('.cart-hair-nudge');
            const image = nudge?.querySelector('.cart-hair-nudge__media img');
            if (!image) return;

            image.src = input.dataset.imageUrl;
            if (input.dataset.imageAlt) image.alt = input.dataset.imageAlt;
        });

        document.addEventListener('change', (e) => {
            const input = e.target.closest('[data-cart-hair-sheet-color-input]');
            if (!input || !input.checked) return;

            const sheet = input.closest('[data-cart-hair-nudge-sheet]');
            updateHairNudgeSheetHero(sheet, input.dataset.imageUrl, input.dataset.imageAlt);
            sheet?.querySelectorAll('[data-cart-hair-sheet-thumb]').forEach((button) => button.classList.remove('is-active'));
        });

        document.addEventListener('cart-hair-nudge-sheet:added', (e) => {
            closeHairNudgeSheet(e.detail?.form?.closest('[data-cart-hair-nudge-sheet]') || e.target.closest('[data-cart-hair-nudge-sheet]'));
        });

        // Prevent form submission on Enter in quantity inputs
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closeHairNudgeSheet(document.querySelector('.cart-hair-nudge-sheet.is-open'));
                document.querySelectorAll('.cart-drawer__policy-stack [data-cart-policy-sheet][aria-hidden="false"]').forEach((sheet) => {
                    setCartPolicySheetState(sheet.closest('.cart-drawer__policy-stack'), false);
                });
            }

            if (e.key === 'Enter') {
                const input = e.target.closest('.quantity__input');
                if (input && input.closest('cart-drawer-items, cart-items')) {
                    e.preventDefault();
                    // Trigger change event instead
                    input.blur();
                }
            }
        }, true);

        // Intercept all quantity input changes in cart drawer and cart page.
        // cart.js defers to CartController when it is present.
        document.addEventListener('change', (e) => {
            const input = e.target.closest('.quantity__input');
            if (input && input.closest('cart-drawer-items, cart-items')) {
                e.stopPropagation();
                e.preventDefault();
                const lineIndex = parseInt(input.dataset.index);
                const newQuantity = parseInt(input.value);
                const itemKey = input.closest('.cart-item')?.dataset?.itemKey;
                if (!isNaN(newQuantity) && this.updateMergedRowQuantity(input.closest('.cart-item'), newQuantity)) return;
                if (!isNaN(lineIndex) && !isNaN(newQuantity)) {
                    this.updateQuantity(lineIndex, newQuantity, itemKey);
                }
            }
        }, true);

        // Intercept quantity button clicks (+/-).
        document.addEventListener('click', (e) => {
            const button = e.target.closest('.quantity__button');
            if (button && button.closest('cart-drawer-items, cart-items')) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation(); // Stop ALL other listeners

                const quantityInput = button.closest('quantity-input');
                if (!quantityInput) return;

                const input = quantityInput.querySelector('.quantity__input');
                if (!input) return;

                const lineIndex = parseInt(input.dataset.index);
                const itemKey = input.closest('.cart-item')?.dataset?.itemKey;
                const currentValue = parseInt(input.value);
                const step = parseInt(input.step) || 1;
                const min = parseInt(input.min) || 0;
                const max = input.max ? parseInt(input.max) : 9999;

                let newValue;
                if (button.name === 'plus') {
                    newValue = Math.min(max, currentValue + step);
                } else if (button.name === 'minus') {
                    newValue = Math.max(min, currentValue - step);
                } else {
                    return;
                }

                if (input.hasAttribute('data-gift-packaging-quantity') && button.name === 'plus' &&
                    !this.canIncreaseGiftPackaging(itemKey, newValue)) return;
                const giftNotice = document.querySelector('[data-cart-gift-limit]');
                if (giftNotice) giftNotice.hidden = true;

                // Update input visually immediately (optimistic UI)
                input.value = newValue;

                // Queue the update
                if (this.updateMergedRowQuantity(input.closest('.cart-item'), newValue)) return;
                this.updateQuantity(lineIndex, newValue, itemKey);
            }
        }, true);

        // Capture bundle disclosure taps before document-level product navigation handlers.
        window.addEventListener('click', (e) => {
            const bundleSummary = e.target.closest('.dt-gift-bundle__details > summary');
            if (bundleSummary) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const details = bundleSummary.parentElement;
                const items = details?.querySelector('.dt-gift-bundle__items');
                if (!details || !items || details.dataset.animating === 'true') return;

                if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                    details.open = !details.open;
                    bundleSummary.setAttribute('aria-expanded', String(details.open));
                    return;
                }

                details.dataset.animating = 'true';
                const finish = (open) => {
                    details.open = open;
                    bundleSummary.setAttribute('aria-expanded', String(open));
                    delete details.dataset.animating;
                    items.style.removeProperty('height');
                    items.style.removeProperty('opacity');
                };
                const finishAfterHeight = (open) => {
                    const handleTransitionEnd = (event) => {
                        if (event.propertyName !== 'height') return;
                        items.removeEventListener('transitionend', handleTransitionEnd);
                        finish(open);
                    };
                    items.addEventListener('transitionend', handleTransitionEnd);
                };

                if (details.open) {
                    items.style.height = `${items.scrollHeight}px`;
                    items.style.opacity = '1';
                    requestAnimationFrame(() => {
                        items.style.height = '0px';
                        items.style.opacity = '0';
                    });
                    finishAfterHeight(false);
                } else {
                    details.open = true;
                    items.style.height = '0px';
                    items.style.opacity = '0';
                    requestAnimationFrame(() => {
                        items.style.height = `${items.scrollHeight}px`;
                        items.style.opacity = '1';
                    });
                    finishAfterHeight(true);
                }
                return;
            }

            const removeButton = e.target.closest('cart-remove-button');
            if (removeButton) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const groupItemKeys = removeButton.dataset.groupItemKeys;
                if (groupItemKeys) {
                    const itemKeys = groupItemKeys.split('||').filter(Boolean);
                    if (itemKeys.length) {
                        const isMergedRow = Boolean(removeButton.closest('[data-merged-item-keys]'));
                        this.removeItemsByKeys(itemKeys);
                        // Same as a single-line remove: let gift/freebie reconciliation see the optimistic cart.
                        if (isMergedRow) this.dispatchCartUpdated();
                        return;
                    }
                }
                const bundleKey = removeButton.dataset.bundleKey;
                if (bundleKey) {
                    if (this.isProcessing) return;
                    this.removeBundle(bundleKey);
                    return;
                }
                const lineIndex = parseInt(removeButton.dataset.index);
                if (!isNaN(lineIndex)) {
                    this.remove(lineIndex, removeButton.dataset.itemKey);
                }
            }
        }, true);

        const syncFromCartData = (cartData) => {
            if (!cartData || !Array.isArray(cartData.items)) return;
            this.cart = cartData;
            this.updateOffersUI(this.getEligibleCartValue(cartData));
        };

        document.addEventListener('cart:updated', (event) => {
            syncFromCartData(event.detail);
        });

        if (!this.cartUpdateUnsubscriber && typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                syncFromCartData(event.cartData);
            });
        }
    },

    beginCartMutation() {
        this.mutationVersion += 1;
        if (this.deferredDrawerRefreshTimer) {
            clearTimeout(this.deferredDrawerRefreshTimer);
            this.deferredDrawerRefreshTimer = null;
        }
        return this.mutationVersion;
    },

    isCurrentMutation(version) {
        return version === undefined || version === this.mutationVersion;
    },

    // ============================================
    // ADD TO CART
    // ============================================
    getOptimisticItemFromForm(form, variantId, quantity = 1) {
        if (!form || !variantId) return null;

        const productForm = form.closest('product-form');
        const dataset = form.dataset?.productTitle ? form.dataset : (productForm?.dataset || {});
        const productCard = form.closest('.card-wrapper, .card, .product-card-wrapper, .product-card, .cart-drawer__recommendation-item');
        const productInfo = form.closest('[data-dt-pdp-ab], product-info, .product, .product__info-container') || productCard || document;
        const selectedVariant = this.getSelectedVariantFromForm(form);
        const selectedVariantMatches = Number(selectedVariant?.id) === Number(variantId);
        const selectedVariantImage = selectedVariantMatches ? this.getVariantImageUrl(selectedVariant) : '';
        const selectedVariantTitle = selectedVariantMatches && selectedVariant?.title !== 'Default Title'
            ? (selectedVariant.public_title || selectedVariant.title || selectedVariant.option1 || '')
            : '';
        const cardTitleLink = productCard?.querySelector('.card__heading a, .cart-drawer__recommendation-title a, .full-unstyled-link');
        const cardImage = productCard?.querySelector('.card__media img, .cart-drawer__recommendation-media img, img');
        const activeProductMediaImage = productInfo.querySelector('.product__media-item.is-active img, .product__media-item[aria-hidden="false"] img, .product__media-list .is-active img, media-gallery [aria-current="true"] img, .dt-pdp-ab__slide:not([aria-hidden="true"]) img');
        const productMediaImage = productInfo.querySelector('.product__media img, .product-media-container img, .product__media-list img, .dt-pdp-ab__slide img');
        const selectedOptionText = this.getElementText(productInfo.querySelector('[data-selected-options]'));
        const selectedSwatch = productInfo.querySelector('[data-single-towel-swatch][aria-pressed="true"]');
        const variantTitle = selectedVariantTitle
            || dataset.productVariantTitle
            || selectedOptionText
            || selectedSwatch?.dataset?.optionValue
            || '';
        const variantOptionName = dataset.productVariantOptionName || (variantTitle && selectedSwatch ? 'Color' : '');
        const price = (selectedVariantMatches ? parseInt(selectedVariant?.price, 10) : 0)
            || parseInt(dataset.productPrice, 10)
            || this.extractMoneyToCents(productInfo.querySelector('.price__sale .price-item--sale, .price-item--sale, .price__regular .price-item--regular, .price .price-item')?.textContent)
            || 0;
        const title = dataset.productTitle
            || this.getElementText(cardTitleLink)
            || this.getElementText(productInfo.querySelector('.product__title h1, h1.product__title, h1, [data-product-title]'));
        const image = selectedVariantImage
            || dataset.productImage
            || activeProductMediaImage?.currentSrc
            || activeProductMediaImage?.src
            || productMediaImage?.currentSrc
            || productMediaImage?.src
            || cardImage?.currentSrc
            || cardImage?.src
            || '';

        if (!title && !price) return null;

        return {
            variant_id: variantId,
            quantity: Math.max(1, parseInt(quantity, 10) || 1),
            product_title: title,
            title,
            url: dataset.productUrl || cardTitleLink?.getAttribute('href') || window.location.pathname,
            image,
            image_alt: dataset.productImageAlt || cardImage?.alt || title,
            variant_title: variantTitle,
            variant_option_name: variantOptionName,
            vendor: dataset.productVendor || '',
            final_price: price,
            price,
            final_line_price: price * (Math.max(1, parseInt(quantity, 10) || 1)),
            original_line_price: price * (Math.max(1, parseInt(quantity, 10) || 1))
        };
    },

    getSelectedVariantFromForm(form) {
        const variantInput = form?.querySelector('[name="id"], .product-variant-id');
        const variantId = variantInput?.value;
        const productForm = form?.closest('product-form');
        const sectionId = productForm?.dataset?.sectionId;
        const sectionRoot = sectionId
            ? Array.from(document.querySelectorAll('[data-dt-pdp-ab][data-section-id]')).find((root) => root.dataset.sectionId === sectionId)
            : null;
        const roots = [
            productForm,
            form.closest('product-info'),
            sectionRoot,
            form.closest('[data-dt-pdp-ab], .product__info-container, .product'),
            form.closest('.card-wrapper, .card, .product-card-wrapper, .product-card')
        ].filter(Boolean);
        const variantSources = roots.flatMap((root) => {
            const sources = [];
            if (root.matches?.('[data-selected-variant], [data-variants]')) sources.push(root);
            sources.push(...Array.from(root.querySelectorAll('[data-selected-variant], [data-variants]')));
            return sources;
        });
        for (const source of variantSources) {
            try {
                const rawData = source.getAttribute('data-variants') || source.getAttribute('data-selected-variant') || source.textContent || '{}';
                const data = JSON.parse(rawData);
                if (Array.isArray(data)) {
                    const variant = data.find((entry) => String(entry?.id) === String(variantId));
                    if (variant) return variant;
                    continue;
                }
                if (data && data.id && (!variantId || String(data.id) === String(variantId))) return data;
            } catch (_) {
                continue;
            }
        }

        const selectedVariantScript = roots.find((root) => root.querySelector('[data-selected-variant]'))?.querySelector('[data-selected-variant]');
        if (!selectedVariantScript) return null;

        try {
            const variant = JSON.parse(selectedVariantScript.textContent || '{}');
            return variant && variant.id ? variant : null;
        } catch (_) {
            return null;
        }
    },

    getVariantImageUrl(variant) {
        const src = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || '';
        if (!src) return '';
        if (src.startsWith('//')) {
            const cdnPath = src.replace(/^\/\/[^/]+/, '');
            return window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost' ? cdnPath : `https:${src}`;
        }
        return src;
    },

    getElementText(element) {
        if (!element) return '';
        return element.textContent?.replace(/\s+/g, ' ')?.trim() || '';
    },

    extractMoneyToCents(text) {
        const match = String(text || '').replace(/\u00a0/g, ' ').match(/(?:Rs\.?|₹|INR)?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i);
        if (!match) return 0;
        const value = parseFloat(match[1].replace(/,/g, ''));
        return Number.isNaN(value) ? 0 : Math.round(value * 100);
    },

    normalizeOptimisticItem(addedItem, fallbackItem, variantId, quantity = 1) {
        const qty = Math.max(1, parseInt(addedItem?.quantity || fallbackItem?.quantity || quantity, 10) || 1);
        const price = Number(addedItem?.final_price || addedItem?.price || fallbackItem?.final_price || fallbackItem?.price || 0);
        const linePrice = Number(addedItem?.final_line_price || fallbackItem?.final_line_price || price * qty || 0);
        const title = addedItem?.product_title || fallbackItem?.product_title || addedItem?.title || fallbackItem?.title || '';

        return {
            variant_id: Number(addedItem?.variant_id || fallbackItem?.variant_id || variantId),
            quantity: qty,
            product_title: title,
            title,
            url: addedItem?.url || fallbackItem?.url || '',
            image: addedItem?.image || fallbackItem?.image || '',
            image_alt: addedItem?.image?.alt || fallbackItem?.image_alt || title,
            variant_title: addedItem?.variant_title || fallbackItem?.variant_title || '',
            variant_option_name: addedItem?.variant_option_name || fallbackItem?.variant_option_name || '',
            vendor: addedItem?.vendor || fallbackItem?.vendor || '',
            properties: addedItem?.properties || fallbackItem?.properties || {},
            compare_at_price: Number(addedItem?.compare_at_price || fallbackItem?.compare_at_price || 0),
            final_price: price,
            price,
            final_line_price: linePrice,
            original_line_price: Number(addedItem?.original_line_price || fallbackItem?.original_line_price || linePrice)
        };
    },

    prepareOptimisticDrawerSurface() {
        const drawer = document.querySelector('cart-drawer');
        const drawerInner = document.querySelector('.drawer__inner');
        const emptyState = document.querySelector('.drawer__inner-empty');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const footer = document.querySelector('.drawer__footer');

        drawer?.classList.remove('is-empty');
        drawerInner?.classList.remove('is-empty');
        cartDrawerItems?.classList.remove('is-empty');
        if (emptyState) emptyState.hidden = true;
        if (footer) footer.hidden = false;
    },

    hasPendingAddSurface() {
        const drawer = document.querySelector('cart-drawer');
        return Boolean(
            drawer?.classList.contains('is-pending-add') ||
            document.querySelector('[data-pending-add-row], .cart-item--optimistic') ||
            this.pendingAddPromise
        );
    },

    formatCartProductTitle(title) {
        const sourceTitle = String(title || '').trim();
        if (!sourceTitle) return '';

        const sourceTitleLower = sourceTitle.toLowerCase();
        const packText = ['12', '8', '6', '4', '3', '2', '1']
            .map((count) => `Pack of ${count}`)
            .find((label) => sourceTitleLower.includes(label.toLowerCase())) || '';

        let displayTitle = sourceTitle.split('|')[0].trim() || sourceTitle;
        displayTitle = displayTitle
            .replace(/\b(?:300|330|400|450|500|600)\s*gsm\b/gi, '')
            .replace(/\b\d+\s*cm?s?\s*x\s*\d+\s*cm?s?\s*x\s*\d+\s*cm?s?\b/gi, '')
            .replace(/\b\d+\s*x\s*\d+\s*cm?s?\b/gi, '')
            .replace(/\s+\|\s+/g, ' ')
            .replace(/\|/g, '')
            .replace(/\s*-\s*,/g, '')
            .replace(/\s*-\s*-/g, '')
            .replace(/--/g, '')
            .replace(/\s+-$/g, '')
            .replace(/\(\)/g, '')
            .replace(/,\s*-/g, '')
            .replace(/\s+,/g, '')
            .replace(/,/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();

        if (displayTitle.endsWith('-')) displayTitle = displayTitle.slice(0, -1).trim();
        if (packText && !displayTitle.toLowerCase().includes(packText.toLowerCase())) {
            displayTitle = `${displayTitle} ${packText}`.trim();
        }
        return displayTitle;
    },

    renderOptimisticFreeGiftBundle({ title, total = 0, originalTotal = 0, items = [], gift = null } = {}) {
        const contents = document.querySelector('cart-drawer #CartDrawer-CartItems');
        if (!contents || !title || !items.length) return;

        let tbody = contents.querySelector('tbody');
        if (!tbody) {
            contents.insertAdjacentHTML('afterbegin', '<div class="drawer__cart-items-wrapper"><table class="cart-items" role="table"><tbody role="rowgroup"></tbody></table></div>');
            tbody = contents.querySelector('tbody');
        }
        if (!tbody) return;

        document.querySelector('[data-optimistic-free-gift-bundle]')?.remove();
        this.prepareOptimisticDrawerSurface();

        const row = document.createElement('tr');
        row.className = 'cart-item dt-gift-bundle-row dt-gift-bundle-row--optimistic';
        row.dataset.optimisticFreeGiftBundle = '';
        const cell = document.createElement('td');
        cell.colSpan = 3;
        const bundle = document.createElement('div');
        bundle.className = 'dt-gift-bundle';

        const banner = document.createElement('div');
        banner.className = 'dt-gift-bundle__banner';
        banner.innerHTML = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="M3 7.25h10v6.25H3V7.25Zm5 0v6.25M2.25 4.75h11.5v2.5H2.25v-2.5ZM8 4.75C5.3 4.75 4.3 3.8 4.3 2.9c0-.7.55-1.15 1.25-1.15C6.75 1.75 8 4.75 8 4.75Zm0 0c2.7 0 3.7-.95 3.7-1.85 0-.7-.55-1.15-1.25-1.15C9.25 1.75 8 4.75 8 4.75Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg><span>FREE GIFT INCLUDED</span>';

        const offer = document.createElement('div');
        offer.className = 'dt-gift-bundle__offer';
        const summary = document.createElement('div');
        summary.className = 'dt-gift-bundle__summary';
        const offerImage = document.createElement('img');
        offerImage.className = 'dt-gift-bundle__offer-image';
        offerImage.src = gift?.image || items[0]?.image || '';
        offerImage.alt = gift?.title || title;
        offerImage.width = 56;
        offerImage.height = 56;
        offerImage.loading = 'lazy';
        offerImage.decoding = 'async';
        offerImage.fetchPriority = 'low';
        const offerTitle = document.createElement('p');
        offerTitle.className = 'dt-gift-bundle__title';
        offerTitle.textContent = title;
        const price = document.createElement('div');
        price.className = 'dt-gift-bundle__price';
        const finalPrice = document.createElement('span');
        finalPrice.textContent = this.formatMoney(total);
        price.appendChild(finalPrice);
        if (originalTotal > total) {
            const originalPrice = document.createElement('s');
            originalPrice.textContent = this.formatMoney(originalTotal);
            price.appendChild(originalPrice);
        }
        summary.append(offerImage, offerTitle, price);

        const actions = document.createElement('div');
        actions.className = 'dt-gift-bundle__actions';
        const details = document.createElement('details');
        details.className = 'dt-gift-bundle__details';
        const detailsSummary = document.createElement('summary');
        const count = items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0) + (gift ? 1 : 0);
        const detailsLabel = document.createElement('span');
        detailsLabel.textContent = `View ${count} products`;
        detailsSummary.appendChild(detailsLabel);
        detailsSummary.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true"><path d="m7.5 5 5 5-5 5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>');
        const itemList = document.createElement('div');
        itemList.className = 'dt-gift-bundle__items';

        [...items, gift].filter(Boolean).forEach((item) => {
            const child = document.createElement('div');
            child.className = 'dt-gift-bundle__item';
            const image = document.createElement('img');
            image.className = 'dt-gift-bundle__item-image';
            image.src = item.image || '';
            image.alt = item.title || '';
            image.width = 32;
            image.height = 32;
            const childTitle = document.createElement('span');
            childTitle.className = 'dt-gift-bundle__item-title';
            childTitle.textContent = `${item.title || ''}${Number(item.quantity) > 1 ? ` × ${item.quantity}` : ''}`;
            const childPrice = document.createElement('span');
            childPrice.className = `dt-gift-bundle__item-price${item === gift ? ' dt-gift-bundle__item-price--free' : ''}`;
            childPrice.textContent = item === gift ? 'FREE' : this.formatMoney((Number(item.price) || 0) * (Number(item.quantity) || 1));
            child.append(image, childTitle, childPrice);
            itemList.appendChild(child);
        });

        details.append(detailsSummary, itemList);
        const removeSlot = document.createElement('button');
        removeSlot.type = 'button';
        removeSlot.className = 'dt-gift-bundle__remove';
        removeSlot.disabled = true;
        removeSlot.setAttribute('aria-label', 'Updating bundle');
        removeSlot.innerHTML = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="m4 4 8 8m0-8-8 8" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>';
        actions.append(details, removeSlot);
        offer.append(summary, actions);
        bundle.append(banner, offer);
        cell.appendChild(bundle);
        row.appendChild(cell);
        tbody.prepend(row);
        this.openDrawer({ skipRefresh: true });
    },

    addOptimisticFreeGiftBundle({ bundleId, title, total = 0, originalTotal = 0, items = [], gift = null } = {}) {
        if (!bundleId || !title || !items.length) return null;
        const previousCart = this.cloneCartData(this.cart);

        if (!this.cart || !Array.isArray(this.cart.items)) {
            this.cart = {
                items: [],
                item_count: 0,
                items_subtotal_price: 0,
                total_price: 0,
                original_total_price: 0,
                total_discount: 0
            };
        }

        const optimisticItems = items.map((item) => {
            const quantity = Math.max(1, Number(item.quantity) || 1);
            const price = Math.max(0, Number(item.price) || 0);
            const compareAtPrice = Math.max(price, Number(item.compareAtPrice) || 0);
            return {
                key: `dt-retention-${bundleId}-${item.id}`,
                variant_id: Number(item.id),
                quantity,
                product_title: item.title || '',
                title: item.title || '',
                variant_title: item.variantTitle || '',
                image: item.image || '',
                properties: { _dt_retention_bundle: bundleId },
                price,
                final_price: price,
                original_price: compareAtPrice,
                final_line_price: price * quantity,
                original_line_price: compareAtPrice * quantity
            };
        });

        if (gift?.variantId) {
            const originalPrice = Math.max(0, Number(gift.originalPrice) || 0);
            optimisticItems.push({
                key: `dt-retention-${bundleId}-gift`,
                variant_id: Number(gift.variantId),
                quantity: 1,
                product_title: gift.title || '',
                title: gift.title || '',
                image: gift.image || '',
                properties: {
                    _dt_retention_bundle: bundleId,
                    _dt_retention_gift: 'pending'
                },
                price: originalPrice,
                final_price: 0,
                original_price: originalPrice,
                final_line_price: 0,
                original_line_price: originalPrice
            });
        }

        const addedQuantity = optimisticItems.reduce((sum, item) => sum + item.quantity, 0);
        const addedOriginalTotal = optimisticItems.reduce((sum, item) => sum + item.original_line_price, 0);
        const giftDiscount = Math.max(0, Number(gift?.originalPrice) || 0);
        this.cart.items.unshift(...optimisticItems);
        this.cart.item_count = (Number(this.cart.item_count) || 0) + addedQuantity;
        this.cart.items_subtotal_price = (Number(this.cart.items_subtotal_price) || 0) + Number(total || 0);
        this.cart.total_price = (Number(this.cart.total_price) || 0) + Number(total || 0);
        this.cart.original_total_price = (Number(this.cart.original_total_price) || 0) + Math.max(Number(originalTotal) || 0, addedOriginalTotal);
        this.cart.total_discount = (Number(this.cart.total_discount) || 0) + giftDiscount;

        this.renderOptimisticFreeGiftBundle({ title, total, originalTotal, items, gift });
        this.updateCartCount();
        this.updateEmptyState();
        this.renderOptimisticPurchaseShell();
        this.updateTotals();
        this.forcePricingUpdate({ refreshDiscountTiers: false }).catch((error) => console.error('[CartController] Optimistic bundle pricing update failed:', error));
        this.updateOffersUI(this.getEligibleCartValue(this.cart));
        this.dispatchCartUpdated();
        return previousCart;
    },

    rollbackOptimisticFreeGiftBundle(previousCart) {
        document.querySelectorAll('[data-optimistic-free-gift-bundle]').forEach((row) => row.remove());
        if (previousCart) this.cart = previousCart;
        this.updateCartCount();
        this.updateEmptyState();
        this.updateTotals();
        this.forcePricingUpdate({ refreshDiscountTiers: false }).catch((error) => console.error('[CartController] Bundle rollback pricing update failed:', error));
        this.updateOffersUI(this.getEligibleCartValue(this.cart));
        this.dispatchCartUpdated();
    },


    renderOptimisticAddedItem(addedItem, fallbackItem, variantId, quantity = 1) {
        const contents = document.querySelector('cart-drawer #CartDrawer-CartItems');
        if (!contents) return;

        let tbody = contents.querySelector('tbody');
        if (!tbody) {
            contents.insertAdjacentHTML('afterbegin', '<div class="drawer__cart-items-wrapper"><table class="cart-items" role="table"><tbody role="rowgroup"></tbody></table></div>');
            tbody = contents.querySelector('tbody');
        }
        if (!tbody) return;

        const item = this.normalizeOptimisticItem(addedItem, fallbackItem, variantId, quantity);
        if (!item.variant_id || !item.product_title) return;

        const existingCartItem = this.cart?.items?.find(cartItem => cartItem.variant_id === item.variant_id && JSON.stringify(cartItem.properties || {}) === JSON.stringify(item.properties || {}));
        const existingInput = existingCartItem?.key
            ? (tbody.querySelector('[data-item-key="' + CSS.escape(existingCartItem.key) + '"] .quantity__input') ||
                [...tbody.querySelectorAll('[data-merged-item-keys]')]
                    .find((mergedRow) => this.getMergedRowKeys(mergedRow).includes(existingCartItem.key))
                    ?.querySelector('.quantity__input'))
            : null;
        const existingRow = existingInput?.closest('.cart-item');
        if (existingRow) {
            const nextQuantity = (parseInt(existingInput.value, 10) || 0) + item.quantity;
            existingInput.value = nextQuantity;
            this.updateLineItemUnitPriceDisplay(existingRow, item);
            return;
        }

        const row = document.createElement('tr');
        row.id = `CartDrawer-Item-pending-${item.variant_id}`;
        row.className = 'cart-item cart-item--optimistic';
        row.setAttribute('role', 'row');
        row.dataset.optimisticVariantId = String(item.variant_id);
        if (existingCartItem?.key) row.dataset.itemKey = existingCartItem.key;
        row.dataset.lineUnitPrice = String(item.final_price);
        row.dataset.lineUnitMrp = String(item.compare_at_price || item.final_price);

        const mediaCell = document.createElement('td');
        mediaCell.className = 'cart-item__media';
        mediaCell.setAttribute('role', 'cell');
        if (item.image) {
            const link = document.createElement('a');
            link.className = 'cart-item__link';
            link.href = item.url || '#';
            link.tabIndex = -1;
            link.setAttribute('aria-hidden', 'true');
            link.textContent = ' ';

            const image = document.createElement('img');
            image.className = 'cart-item__image';
            image.src = item.image;
            image.alt = item.image_alt || item.product_title;
            image.loading = 'lazy';
            image.width = 112;
            image.height = 112;

            mediaCell.append(link, image);
        }

        const detailsCell = document.createElement('td');
        detailsCell.className = 'cart-item__details';
        detailsCell.setAttribute('role', 'cell');
        const titleLink = document.createElement('a');
        titleLink.className = 'cart-item__name h4';
        titleLink.href = item.url || '#';
        titleLink.textContent = this.formatCartProductTitle(item.product_title);
        detailsCell.appendChild(titleLink);
        const festiveRule = window.DtGiftPackagingRules?.product(item.url);
        if (festiveRule?.weights.large === 4 && (festiveRule.packSize === 4 || (/(?:alu-vera|aluvera|aloevera)-double-cloth-bath-towel/.test(item.url) && [8, 12].includes(festiveRule.packSize))) && item.properties?.['Festive packaging'] !== 'Not included') {
            const note = document.createElement('p');
            note.className = 'product-option';
            note.dataset.festivePackagingNote = '';
            note.textContent = 'Free festive packaging included';
            detailsCell.appendChild(note);
        }
        const isPackaging = item.url.includes('/products/gift-packaging-add-on');
        if (isPackaging) {
            const option = document.createElement('p');
            option.className = 'product-option';
            option.textContent = item.variant_title;
            detailsCell.appendChild(option);
        } else if (item.variant_title && item.variant_title !== 'Default Title') {
            const optionList = document.createElement('dl');
            const option = document.createElement('div');
            option.className = 'product-option';
            const name = document.createElement('dt');
            name.textContent = `${item.variant_option_name || 'Color'}: `;
            const value = document.createElement('dd');
            value.textContent = item.variant_title;
            option.append(name, value);
            optionList.appendChild(option);
            detailsCell.appendChild(optionList);
        }

        const quantityCell = document.createElement('td');
        quantityCell.className = 'cart-item__quantity';
        quantityCell.setAttribute('role', 'cell');
        quantityCell.innerHTML = `
            <div class="cart-item__price-wrapper-inline">
                <span class="price light-text cart-item__final-price">${this.formatMoney(item.final_price)}</span>
                ${item.compare_at_price > item.final_price ? `<s class="cart-item__old-price price light-text">${this.formatMrpMoney(item.compare_at_price)}</s>` : ''}
            </div>
            <quantity-popover>
                <div class="cart-item__quantity-wrapper quantity-popover-wrapper">
                    <div class="quantity-popover-container">
                        <quantity-input class="quantity cart-quantity">
                            <button class="quantity__button" name="minus" type="button" disabled aria-hidden="true">
                                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path></svg>
                            </button>
                            <input class="quantity__input" type="number" data-quantity-variant-id="${item.variant_id}" name="updates[]" value="${item.quantity}" min="0" disabled>
                            <button class="quantity__button" name="plus" type="button" disabled aria-hidden="true">
                                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path></svg>
                            </button>
                        </quantity-input>
                    </div>
                    <cart-remove-button>
                        <button type="button" class="button button--tertiary cart-remove-button" disabled aria-hidden="true">
                            <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path><path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                        </button>
                    </cart-remove-button>
                </div>
            </quantity-popover>
        `;

        if (isPackaging) {
            quantityCell.inert = true;
            const priceWrapper = quantityCell.querySelector('.cart-item__price-wrapper-inline');
            if (item.compare_at_price > item.final_price) {
                const discountedPrices = document.createElement('div');
                discountedPrices.className = 'cart-item__discounted-prices';
                discountedPrices.append(...priceWrapper.childNodes);
                priceWrapper.appendChild(discountedPrices);
            }
            quantityCell.querySelectorAll('[disabled]').forEach(control => control.removeAttribute('disabled'));
        }
        row.append(mediaCell, detailsCell, quantityCell);
        tbody.prepend(row);
    },

    renderPendingAddedItem(variantId, quantity = 1) {
        const contents = document.querySelector('cart-drawer #CartDrawer-CartItems');
        if (!contents || contents.querySelector('[data-pending-add-row]')) return;

        let tbody = contents.querySelector('tbody');
        if (!tbody) {
            contents.insertAdjacentHTML('afterbegin', '<div class="drawer__cart-items-wrapper"><table class="cart-items" role="table"><tbody role="rowgroup"></tbody></table></div>');
            tbody = contents.querySelector('tbody');
        }
        if (!tbody) return;

        const qty = Math.max(1, parseInt(quantity, 10) || 1);
        tbody.prepend(document.createRange().createContextualFragment(`
            <tr class="cart-item cart-item--pending-add" role="row" data-pending-add-row data-pending-variant-id="${Number(variantId) || ''}">
              <td class="cart-item__media" role="cell">
                <span class="cart-drawer__pending-media"></span>
              </td>
              <td class="cart-item__details" role="cell">
                <span class="cart-drawer__pending-line cart-drawer__pending-line--title"></span>
                <span class="cart-drawer__pending-line cart-drawer__pending-line--meta"></span>
              </td>
              <td class="cart-item__quantity" role="cell">
                <span class="cart-drawer__pending-line cart-drawer__pending-line--price"></span>
                <span class="cart-drawer__pending-quantity">${qty}</span>
              </td>
            </tr>
        `));
    },

    removePendingAddedItem() {
        document.querySelectorAll('[data-pending-add-row]').forEach((row) => row.remove());
    },

    applyOptimisticAdd(item, variantId, quantity = 1) {
        const optimisticItem = this.normalizeOptimisticItem(null, item, variantId, quantity);
        if (!optimisticItem.variant_id) return;

        if (!this.cart || !Array.isArray(this.cart.items)) {
            this.cart = {
                items: [],
                item_count: 0,
                items_subtotal_price: 0,
                total_price: 0,
                total_discount: 0
            };
        }

        const existingItem = this.cart.items.find((cartItem) => cartItem.variant_id === optimisticItem.variant_id && JSON.stringify(cartItem.properties || {}) === JSON.stringify(optimisticItem.properties || {}));
        if (existingItem) {
            const nextQuantity = (Number(existingItem.quantity) || 0) + optimisticItem.quantity;
            const unitPrice = Number(existingItem.final_price || optimisticItem.final_price || optimisticItem.price || 0);
            existingItem.quantity = nextQuantity;
            existingItem.final_line_price = unitPrice * nextQuantity;
            existingItem.original_line_price = Number(existingItem.original_price || existingItem.price || unitPrice) * nextQuantity;
        } else {
            optimisticItem.key = `pending-${optimisticItem.variant_id}-${JSON.stringify(optimisticItem.properties)}`;
            this.cart.items.unshift(optimisticItem);
        }

        this.cart.item_count = (Number(this.cart.item_count) || 0) + optimisticItem.quantity;
        this.cart.items_subtotal_price = (Number(this.cart.items_subtotal_price) || 0) + optimisticItem.final_line_price;
        this.cart.total_price = (Number(this.cart.total_price) || 0) + optimisticItem.final_line_price;
    },

    normalizeDrawerSectionOrder() {
        const root = document.querySelector('#CartDrawer .cart_overflow_inner');
        const cartItems = root?.querySelector('cart-drawer-items');
        if (!cartItems) return;

        let anchor = cartItems;
        [
            root.querySelector('.offers-section'),
            root.querySelector('.dt-cart-flash'),
            root.querySelector('.cart-drawer__assurance-strip'),
            root.querySelector('.cart-drawer__customers-bought'),
            root.querySelector('.cart-drawer__price-breakdown')
        ].forEach((section) => {
            if (!section) return;
            anchor.insertAdjacentElement('afterend', section);
            anchor = section;
        });
    },

    renderOptimisticPurchaseShell() {
        if (!this.cart?.items?.length) return;
        if (document.querySelector('.cart-drawer__price-breakdown')) {
            this.normalizeDrawerSectionOrder();
            return;
        }

        const cartItems = document.querySelector('.cart_overflow_inner cart-drawer-items');
        if (!cartItems) return;

        const tierEligibleValue = this.getEligibleCartValue(this.cart);
        const tierDiscount = this.getTierDiscount(tierEligibleValue);
        const bundlePricingTotals = this.getBundlePricingTotals(this.cart);
        const bundleAwareTotal = bundlePricingTotals.bundleAwareTotal;
        const youPay = Math.max(0, bundleAwareTotal - tierDiscount);
        const limitedDiscount = this.calculateComplementaryDiscount(this.cart);
        const bundleDiscount = Math.max(0, bundlePricingTotals.originalBundleTotal - bundlePricingTotals.fixedBundleTotal);
        const mrpTotal = Math.max(youPay + limitedDiscount + bundleDiscount + tierDiscount, youPay);

        cartItems.insertAdjacentHTML('afterend', `
            <div class="cart-drawer__price-breakdown cart-drawer__price-breakdown--optimistic">
              <h3 class="cart-drawer__price-breakdown-heading">Price Breakdown</h3>
              <div class="cart-drawer__price-breakdown-content">
                <div class="price-breakdown__row">
                  <span class="price-breakdown__label">MRP</span>
                  <span class="price-breakdown__value" data-cart-mrp>${this.formatMoney(mrpTotal)}</span>
                </div>
                <div class="price-breakdown__row">
                  <span class="price-breakdown__label">Delivery Fee</span>
                  <span class="price-breakdown__value"><span style="text-decoration: line-through; color: rgba(0, 0, 0, 0.4);">Rs. 150</span></span>
                </div>
                <div class="price-breakdown__row" data-discount-row style="${limitedDiscount > 0 ? '' : 'display: none;'}">
                  <span class="price-breakdown__label">Limited time discount</span>
                  <span class="price-breakdown__value" style="color: rgb(76, 175, 80);" data-complementary-discount>${limitedDiscount > 0 ? `-${this.formatMoney(limitedDiscount)}` : ''}</span>
                </div>
                <div class="price-breakdown__row" data-tier-row style="${tierDiscount > 0 ? '' : 'display: none;'}">
                  <span class="price-breakdown__label">
                    Buy More Save More
                    <button type="button" class="price-breakdown__info-icon" aria-label="Buy More Save More information" aria-expanded="false" aria-controls="tier-info-optimistic">${this.getInfoIconMarkup()}</button>
                  </span>
                  <span class="price-breakdown__value" style="color: rgb(76, 175, 80);" data-tier-discount>${tierDiscount > 0 ? `-${this.formatMoney(tierDiscount)}` : ''}</span>
                </div>
                <div class="price-breakdown__info-section" id="tier-info-optimistic" role="region" hidden>
                  <div class="price-breakdown__info-section-content">Bundles Offers are not counted in Buy More Save More. This discount is calculated only on normal product cart value.</div>
                </div>
                <div class="price-breakdown__row" data-bundle-discount-row style="${bundleDiscount > 0 ? '' : 'display: none;'}">
                  <span class="price-breakdown__label">Bundle Discount</span>
                  <span class="price-breakdown__value" style="color: rgb(76, 175, 80);" data-bundle-discount>${bundleDiscount > 0 ? `-${this.formatMoney(bundleDiscount)}` : ''}</span>
                </div>
                <div class="price-breakdown__row price-breakdown__row--total">
                  <span class="price-breakdown__label" style="font-weight: 600;">You Pay</span>
                  <span class="price-breakdown__value" style="font-weight: 600; font-size: calc(var(--font-body-scale) * 16px);" data-cart-breakdown-total>${this.formatMoney(youPay)}</span>
                </div>
              </div>
            </div>
        `);
        this.normalizeDrawerSectionOrder();
    },

    renderOptimisticMerchandisingShell() {
        if ((!this.cart?.items?.length && !document.querySelector('[data-pending-add-row]')) || document.querySelector('[data-optimistic-customers-bought]')) return;

        const cartItems = document.querySelector('.cart_overflow_inner cart-drawer-items');
        if (!cartItems) return;

        const existingCustomers = document.querySelectorAll('.cart_overflow_inner .cart-drawer__customers-bought:not([data-optimistic-customers-bought])');
        const hasCustomersInPlace = Array.from(existingCustomers).some((section) => section.previousElementSibling === cartItems);
        if (hasCustomersInPlace) return;
        existingCustomers.forEach((section) => section.remove());

        cartItems.insertAdjacentHTML('afterend', `
            <section class="cart-drawer__recommendations cart-drawer__customers-bought cart-drawer__customers-bought--optimistic" data-optimistic-merchandising data-optimistic-customers-bought aria-hidden="true">
              <div class="cart-drawer__optimistic-heading-row">
                <h3 class="cart-drawer__recommendations-heading">Customers also bought</h3>
                <span class="cart-drawer__optimistic-status">Loading picks</span>
              </div>
              <div class="cart-drawer__recommendations-scroll cart-drawer__optimistic-products">
                <span class="cart-drawer__optimistic-product">
                  <span class="cart-drawer__optimistic-product-media"></span>
                  <span class="cart-drawer__optimistic-product-line cart-drawer__optimistic-product-line--title"></span>
                  <span class="cart-drawer__optimistic-product-line cart-drawer__optimistic-product-line--price"></span>
                  <span class="cart-drawer__optimistic-product-button"></span>
                </span>
                <span class="cart-drawer__optimistic-product">
                  <span class="cart-drawer__optimistic-product-media"></span>
                  <span class="cart-drawer__optimistic-product-line cart-drawer__optimistic-product-line--title"></span>
                  <span class="cart-drawer__optimistic-product-line cart-drawer__optimistic-product-line--price"></span>
                  <span class="cart-drawer__optimistic-product-button"></span>
                </span>
                <span class="cart-drawer__optimistic-product">
                  <span class="cart-drawer__optimistic-product-media"></span>
                  <span class="cart-drawer__optimistic-product-line cart-drawer__optimistic-product-line--title"></span>
                  <span class="cart-drawer__optimistic-product-line cart-drawer__optimistic-product-line--price"></span>
                  <span class="cart-drawer__optimistic-product-button"></span>
                </span>
              </div>
            </section>
        `);
    },

    isStableMerchandisingAddSource(sourceForm) {
        return Boolean(sourceForm?.closest?.('.cart-drawer__customers-bought, .cart-drawer-deals-banner, .cart-deals-products'));
    },

    async add(variantId, quantity = 1, properties = {}, options = {}) {
        const optimisticItem = options.optimisticItem || this.getOptimisticItemFromForm(options.sourceForm, variantId, quantity);
        if (optimisticItem) {
            return this.queueOptimisticAdd(variantId, quantity, properties, options, optimisticItem);
        }

        if (this.isProcessing) {
            console.log('[CartController] Already processing, queuing add...');
            // Still allow the add, but wait briefly
            await this.waitForProcessing();
        }

        const previousCart = this.cloneCartData(this.cart);
        const mutationVersion = this.beginCartMutation();
        const refreshRecommendations = !this.isStableMerchandisingAddSource(options.sourceForm);
        this.isProcessing = true;
        this.showLoading();
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
            drawer.classList.add('is-pending-add');
        }
        this.prepareOptimisticDrawerSurface();
        this.renderPendingAddedItem(variantId, quantity);
        this.renderOptimisticSavingsProgress({ allowPending: true });
        this.updateSavingsProgressUI(0);
        if (refreshRecommendations) {
            this.renderOptimisticMerchandisingShell();
        }
        this.openDrawer({ skipRefresh: true });

        try {
            await this.syncPendingRemovalsBeforeAdd();

            const item = {
                    id: variantId,
                    quantity: quantity,
                    properties: properties
                };
            if (options.sellingPlanId) item.selling_plan = options.sellingPlanId;

            const formData = {
                items: [item, ...(options.additionalItems || [])]
            };

            const doFetch = () =>
                fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
            const withRetry = window.cartAddFetchWithRetry
                ? (fn) => window.cartAddFetchWithRetry(fn)
                : (fn) => fn();
            const response = await (window.cartAddQueue
                ? window.cartAddQueue(() => withRetry(doFetch))
                : withRetry(doFetch));

            if (!response.ok) {
                let errorMsg = 'Could not add to cart';
                try {
                    const error = await response.json();
                    errorMsg = error.description || errorMsg;
                } catch (_) {}
                if (response.status === 429) {
                    errorMsg = 'Too many requests. Please wait a moment and try again.';
                }
                console.error('[CartController] Add failed:', response.status, errorMsg);
                await this.rollbackFailedAdd(previousCart, options.sourceForm, errorMsg, mutationVersion);
                this.showError(errorMsg);
                return false;
            }

            const responseData = await response.json().catch(() => null);
            if (!responseData || (Array.isArray(responseData.items) && responseData.items.length === 0)) {
                const errorMsg = window.cartStrings?.error || 'Could not add this item to cart. Please try again.';
                console.error('[CartController] Add returned no cart items. The variant may be blocked by product purchase settings.');
                await this.rollbackFailedAdd(previousCart, options.sourceForm, errorMsg, mutationVersion);
                this.showError(errorMsg);
                return false;
            }
            if (!this.isCurrentMutation(mutationVersion)) return true;

            await this.refreshCart();
            if (!this.isCurrentMutation(mutationVersion)) return true;

            await this.refreshCartDrawerHTML({
                refreshRecommendations,
                refreshItems: true,
                mutationVersion
            });
            if (!this.isCurrentMutation(mutationVersion)) return true;

            this.updateAllUI();
            if (options.refreshCartPage !== false) {
                await this.refreshCartPageHTML();
            }

            if (drawer) {
                drawer.classList.remove('is-pending-add');
            }
            if (!optimisticItem) this.openDrawer({ skipRefresh: true });

            // Fire tracking events for abandoned cart recovery (GA4, Facebook, Nitro)
            this.fireAddToCartTracking();

            return true;

        } catch (error) {
            console.error('[CartController] Add error:', error);
            if (previousCart) {
                this.cart = previousCart;
                this.updateCartCount();
                this.updateEmptyState();
                this.updateTotals();
            }
            await this.rollbackFailedAdd(previousCart, options.sourceForm, 'Something went wrong', mutationVersion);
            this.showError('Something went wrong');
            return false;
        } finally {
            const drawer = document.querySelector('cart-drawer');
            if (drawer) {
                drawer.classList.remove('is-pending-add');
            }
            this.isProcessing = false;
            this.hideLoading();
        }
    },

    queueOptimisticAdd(variantId, quantity = 1, properties = {}, options = {}, optimisticItem) {
        optimisticItem = { ...optimisticItem, properties };
        const mutationVersion = this.beginCartMutation();
        const drawer = document.querySelector('cart-drawer');

        this.clearError();
        if (drawer) drawer.classList.add('is-pending-add');

        this.applyOptimisticAdd(optimisticItem, variantId, quantity);
        (options.additionalItems || []).forEach(item => {
            if (!item.optimisticItem) return;
            this.applyOptimisticAdd({ ...item.optimisticItem, properties: item.properties }, item.id, item.quantity);
        });
        this.prepareOptimisticDrawerSurface();
        this.updateCartCount();
        this.updateEmptyState();
        this.renderOptimisticSavingsProgress();
        this.renderOptimisticAddedItem(null, optimisticItem, variantId, quantity);
        (options.additionalItems || []).forEach(item => {
            if (!item.optimisticItem) return;
            this.renderOptimisticAddedItem(null, { ...item.optimisticItem, properties: item.properties }, item.id, item.quantity);
        });
        this.renderOptimisticPurchaseShell();
        if (!this.isStableMerchandisingAddSource(options.sourceForm)) {
            this.renderOptimisticMerchandisingShell();
        }
        this.updateOffersUI(this.getEligibleCartValue(this.cart));
        this.forcePricingUpdate();
        this.dispatchCartUpdated();
        if (drawer) drawer.classList.remove('is-pending-add');
        this.openDrawer({ skipRefresh: true });

        this.pendingAddQueue.push({
            variantId,
            quantity,
            properties: properties || {},
            sellingPlanId: options.sellingPlanId || '',
            sourceForm: options.sourceForm || null,
            preserveRecommendations: this.isStableMerchandisingAddSource(options.sourceForm)
        });
        (options.additionalItems || []).forEach((item) => {
            this.pendingAddQueue.push({ variantId: item.id, quantity: item.quantity, properties: item.properties || {} });
        });
        this.scheduleAddSync(mutationVersion);
        return true;
    },

    scheduleAddSync(mutationVersion) {
        if (this.pendingAddTimer) {
            clearTimeout(this.pendingAddTimer);
        }

        this.pendingAddTimer = setTimeout(() => {
            this.pendingAddTimer = null;
            this.flushPendingAdds(mutationVersion);
        }, 280);
    },

    getPendingAddItems(adds) {
        const grouped = new Map();
        adds.forEach((entry) => {
            const variantId = Number(entry.variantId);
            const quantity = Math.max(1, parseInt(entry.quantity, 10) || 1);
            if (!variantId) return;
            const properties = entry.properties || {};
            const sellingPlanId = entry.sellingPlanId || '';
            const groupKey = `${variantId}:${sellingPlanId}:${JSON.stringify(properties)}`;
            const current = grouped.get(groupKey);
            if (current) {
                current.quantity += quantity;
            } else {
                const item = {
                    id: variantId,
                    quantity,
                    properties
                };
                if (sellingPlanId) item.selling_plan = sellingPlanId;
                grouped.set(groupKey, item);
            }
        });
        return Array.from(grouped.values());
    },

    async flushPendingAdds(mutationVersion = this.mutationVersion) {
        if (this.pendingAddTimer) {
            clearTimeout(this.pendingAddTimer);
            this.pendingAddTimer = null;
        }
        if (this.pendingAddPromise) {
            return this.pendingAddPromise.then((synced) => {
                if (!synced) return false;
                return this.pendingAddQueue.length ? this.flushPendingAdds(this.mutationVersion) : true;
            });
        }
        if (!this.pendingAddQueue.length) return Promise.resolve(true);

        const addsToSync = this.pendingAddQueue.splice(0);
        const items = this.getPendingAddItems(addsToSync);
        const sourceForm = addsToSync.find((entry) => entry.sourceForm)?.sourceForm || null;
        const refreshRecommendations = !addsToSync.some((entry) => entry.preserveRecommendations);

        this.pendingAddPromise = (async () => {
            try {
                if (!items.length) return true;
                await this.syncPendingRemovalsBeforeAdd();

                const doFetch = () =>
                    fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items })
                    });
                const withRetry = window.cartAddFetchWithRetry
                    ? (fn) => window.cartAddFetchWithRetry(fn)
                    : (fn) => fn();
                const response = await (window.cartAddQueue
                    ? window.cartAddQueue(() => withRetry(doFetch))
                    : withRetry(doFetch));

                if (!response.ok) {
                    let errorMsg = 'Could not add to cart';
                    try {
                        const error = await response.json();
                        errorMsg = error.description || errorMsg;
                    } catch (_) {}
                    if (response.status === 429) {
                        errorMsg = 'Too many requests. Please wait a moment and try again.';
                    }
                    throw new Error(errorMsg);
                }

                const responseData = await response.json().catch(() => null);
                if (!responseData || (Array.isArray(responseData.items) && responseData.items.length === 0)) {
                    console.error('[CartController] Add sync returned no cart items. The variant may be blocked by product purchase settings.');
                    throw new Error(window.cartStrings?.error || 'Could not add this item to cart. Please try again.');
                }
                if (!this.isCurrentMutation(mutationVersion)) return true;

                await this.refreshCart();
                if (!this.isCurrentMutation(mutationVersion)) return true;

                await this.refreshCartDrawerHTML({
                    refreshRecommendations,
                    refreshItems: true,
                    mutationVersion
                });
                if (!this.isCurrentMutation(mutationVersion)) return true;

                this.updateAllUI();
                await this.refreshCartPageHTML();
                this.fireAddToCartTracking();
                this.clearError();
                return true;
            } catch (error) {
                console.error('[CartController] Add sync error:', error);
                this.removePendingAddedItem();
                await this.refreshCart();
                this.updateAllUI();
                this.updateCartCount();
                this.updateEmptyState();
                this.updateTotals();
                await this.refreshCartDrawerHTML({
                    refreshRecommendations: false,
                    refreshItems: true,
                    mutationVersion
                });
                await this.refreshCartPageHTML();
                this.showProductFormError(sourceForm, error.message || 'Could not add to cart');
                this.showError(error.message || 'Could not add to cart');
                return false;
            } finally {
                this.pendingAddPromise = null;
                if (this.pendingAddQueue.length) {
                    this.scheduleAddSync(this.mutationVersion);
                }
            }
        })();

        return this.pendingAddPromise;
    },

    async syncPendingRemovalsBeforeAdd() {
        if (this.pendingRemovalTimer) {
            clearTimeout(this.pendingRemovalTimer);
            this.pendingRemovalTimer = null;
        }

        if (this.pendingRemovalPromise) {
            await this.pendingRemovalPromise.catch(() => false);
        }

        if (!this.pendingRemovalKeys.size) return true;

        const keysToRemove = Array.from(this.pendingRemovalKeys);
        const updates = keysToRemove.reduce((acc, key) => {
            if (key) acc[key] = 0;
            return acc;
        }, {});

        if (!Object.keys(updates).length) return true;

        try {
            const doFetch = () =>
                fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });
            const withRetry = window.cartAddFetchWithRetry
                ? (fn) => window.cartAddFetchWithRetry(fn)
                : (fn) => fn();
            const response = await (window.cartMutationQueue
                ? window.cartMutationQueue(() => withRetry(doFetch))
                : withRetry(doFetch));
            if (!response.ok) throw new Error(`Remove-before-add sync failed with status ${response.status}`);
            await response.json().catch(() => null);
            keysToRemove.forEach((key) => this.pendingRemovalKeys.delete(key));
            return true;
        } catch (error) {
            console.error('[CartController] Remove-before-add sync error:', error);
            return false;
        }
    },

    async flushPendingCartMutations() {
        const removalsSynced = await this.flushPendingRemovals();
        if (!removalsSynced) return false;
        const quantitiesSynced = await this.flushQuantityUpdates();
        if (!quantitiesSynced) return false;
        return this.flushPendingAdds();
    },

    dispatchCartUpdated() {
        if (!this.cart) return;

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: this.cart }));
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-controller',
                cartData: this.cart
            });
        }
    },

    renderOptimisticSavingsProgress(options = {}) {
        const allowPending = Boolean(options.allowPending);
        const hasCartItems = Boolean(this.cart && this.cart.item_count > 0);
        if (!hasCartItems && (!allowPending || !document.querySelector('[data-pending-add-row]'))) return;

        const container = document.querySelector('.cart_overflow_inner');
        if (!container) return;
        const existingCard = container.querySelector('[data-cart-savings-progress]');
        if (existingCard) {
            existingCard.hidden = false;
            return;
        }

        container.insertAdjacentHTML('afterbegin', `
            <span class="cart-savings-progress__sticky-sentinel" data-cart-savings-sticky-sentinel aria-hidden="true"></span>
            <section
              class="cart-savings-progress cart-savings-progress--optimistic"
              data-cart-savings-progress
              data-cart-value="0"
              data-max-threshold="2000000"
            >
              <div class="cart-savings-progress__full" data-cart-savings-full>
                <div class="cart-savings-progress__inner">
                  <div class="cart-savings-progress__copy">
                    <div class="cart-savings-progress__header">
                      <h3 class="cart-savings-progress__heading">Buy More, Save More</h3>
                      <span class="cart-savings-progress__value" data-cart-savings-value>&#8377;0 in cart</span>
                    </div>
                  </div>
                  <div class="cart-savings-progress__bar-wrap">
                    <div class="cart-savings-progress__percent-row" aria-hidden="true">
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="300000" style="--marker-position: 10px;">5%</span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="500000" style="--marker-position: calc(33.333% + 3.333px);">10%</span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="1000000" style="--marker-position: calc(66.667% - 3.333px);">15%</span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="2000000" style="--marker-position: calc(100% - 10px);">20%</span>
                    </div>
                    <div
                      class="cart-savings-progress__bar"
                      data-cart-savings-bar
                      role="progressbar"
                      aria-valuemin="0"
                      aria-valuemax="20000"
                      aria-valuenow="0"
                      aria-label="Cart value progress toward cart offers"
                    >
                      <div class="cart-savings-progress__bar-fill" data-cart-savings-fill style="--cart-savings-fill-scale: 0;"></div>
                      <span class="cart-savings-progress__segment" aria-hidden="true" style="--segment-left: 46px; --segment-width: calc(33.333% - 64px);"></span>
                      <span class="cart-savings-progress__segment" aria-hidden="true" style="--segment-left: calc(33.333% + 32px); --segment-width: calc(33.334% - 64px);"></span>
                      <span class="cart-savings-progress__segment" aria-hidden="true" style="--segment-left: calc(66.667% + 18px); --segment-width: calc(33.333% - 64px);"></span>
                      <span class="cart-savings-progress__segment-fill" data-cart-savings-segment-fill data-segment-index="0" aria-hidden="true" style="--segment-left: 46px; --segment-width: calc(33.333% - 64px); --segment-fill-scale: 0;"></span>
                      <span class="cart-savings-progress__segment-fill" data-cart-savings-segment-fill data-segment-index="1" aria-hidden="true" style="--segment-left: calc(33.333% + 32px); --segment-width: calc(33.334% - 64px); --segment-fill-scale: 0;"></span>
                      <span class="cart-savings-progress__segment-fill" data-cart-savings-segment-fill data-segment-index="2" aria-hidden="true" style="--segment-left: calc(66.667% + 18px); --segment-width: calc(33.333% - 64px); --segment-fill-scale: 0;"></span>
                      <span class="cart-savings-progress__marker" data-cart-savings-marker data-threshold="300000" style="--marker-position: 21px; --milestone-fill: 0%;"></span>
                      <span class="cart-savings-progress__marker" data-cart-savings-marker data-threshold="500000" style="--marker-position: calc(33.333% + 7px);"></span>
                      <span class="cart-savings-progress__marker" data-cart-savings-marker data-threshold="1000000" style="--marker-position: calc(66.667% - 7px);"></span>
                      <span class="cart-savings-progress__marker cart-savings-progress__marker--last" data-cart-savings-marker data-threshold="2000000" style="--marker-position: calc(100% - 21px);"></span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="300000" style="--marker-position: 21px;">5%</span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="500000" style="--marker-position: calc(33.333% + 7px);">10%</span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="1000000" style="--marker-position: calc(66.667% - 7px);">15%</span>
                      <span class="cart-savings-progress__percent" data-cart-savings-percent data-threshold="2000000" style="--marker-position: calc(100% - 21px);">20%</span>
                    </div>
                    <div class="cart-savings-progress__labels" aria-hidden="true">
                      <span class="cart-savings-progress__label" data-cart-savings-label data-threshold="300000" style="--marker-position: 21px;"><span class="cart-savings-progress__label-main">5%</span><span class="cart-savings-progress__label-sub">&#8377;3k</span></span>
                      <span class="cart-savings-progress__label" data-cart-savings-label data-threshold="500000" style="--marker-position: calc(33.333% + 7px);"><span class="cart-savings-progress__label-main">10%</span><span class="cart-savings-progress__label-sub">&#8377;5k</span></span>
                      <span class="cart-savings-progress__label" data-cart-savings-label data-threshold="1000000" style="--marker-position: calc(66.667% - 7px);"><span class="cart-savings-progress__label-main">15%</span><span class="cart-savings-progress__label-sub">&#8377;10k</span></span>
                      <span class="cart-savings-progress__label cart-savings-progress__label--last" data-cart-savings-label data-threshold="2000000" style="--marker-position: calc(100% - 21px);"><span class="cart-savings-progress__label-main">20%</span><span class="cart-savings-progress__label-sub">&#8377;20k</span></span>
                    </div>
                  </div>
                  <p class="cart-savings-progress__message" data-cart-savings-message></p>
                </div>
              </div>
              <div class="cart-savings-progress__compact" data-cart-savings-compact aria-hidden="true">
                <p class="cart-savings-progress__compact-message" data-cart-savings-message></p>
              </div>
            </section>
            <span class="cart-savings-progress__space" data-cart-savings-space aria-hidden="true"></span>
        `);
    },

    // Fire AddToCart tracking for GA4, Facebook Pixel, Nitro abandoned-cart recovery
    // Call with cartData from cart-controller (uses this.cart) or from cart-drawer (pass parsedState)
    fireAddToCartTracking(cartData) {
        const cart = cartData || this.cart;
        if (!cart || !cart.items || cart.items.length === 0) return;
        try {
            // GA4 / GTM dataLayer (queued; processed when gtag loads)
            if (typeof window.dataLayer !== 'undefined') {
                window.dataLayer.push({
                    event: 'add_to_cart',
                    ecommerce: {
                        currency: cart.currency,
                        value: (cart.total_price || 0) / 100,
                        items: cart.items.map((item) => ({
                            item_id: String(item.variant_id),
                            item_name: item.product_title,
                            quantity: item.quantity,
                            price: (item.final_price || 0) / 100,
                        })),
                    },
                });
            }
            // Facebook Pixel (if loaded)
            if (typeof window.fbq === 'function') {
                window.fbq('track', 'AddToCart', {
                    content_ids: cart.items.map((i) => String(i.variant_id)),
                    content_type: 'product',
                    value: (cart.total_price || 0) / 100,
                    currency: cart.currency,
                });
            }
            // gtag direct (if loaded)
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'add_to_cart', {
                    currency: cart.currency,
                    value: (cart.total_price || 0) / 100,
                    items: cart.items.map((item) => ({
                        id: String(item.variant_id),
                        name: item.product_title,
                        quantity: item.quantity,
                        price: (item.final_price || 0) / 100,
                    })),
                });
            }
        } catch (e) {
            console.warn('[CartController] Tracking fire failed:', e);
        }
    },

    // ============================================
    // UPDATE QUANTITY (Debounced for bulk changes)
    // ============================================
    canIncreaseGiftPackaging(itemKey, quantity) {
        const items = this.cart?.items || [];
        const state = window.DtGiftPackagingRules?.cartState(items, itemKey, quantity);
        if (state && !Object.keys(state.updates).length) return true;
        const notice = document.querySelector('[data-cart-gift-limit]');
        if (notice) {
            const packaging = items.find(item => item.key === itemKey);
            const towel = items.find(item => String(item.variant_id || item.id) === String(packaging?.properties?._gift_for_variant));
            if (window.DtGiftPackagingRules?.product(towel?.handle) && notice.firstChild?.nodeType === 3) {
                notice.firstChild.textContent = 'You’ve reached the limit for this packaging type. Increase the towel quantity or ';
            }
            notice.hidden = false;
        }
        return false;
    },

    // A merged row shows several Shopify lines of one product (split by a discount allocation).
    getMergedRowKeys(row) {
        return String(row?.dataset?.mergedItemKeys || '').split('||').filter(Boolean);
    },

    getRowDisplayQuantity(row, fallbackQuantity) {
        const keys = this.getMergedRowKeys(row);
        if (!keys.length || !Array.isArray(this.cart?.items)) return fallbackQuantity;
        return keys.reduce((total, key) => {
            const line = this.cart.items.find((cartItem) => cartItem.key === key);
            return total + (line ? Math.max(0, parseInt(line.quantity, 10) || 0) : 0);
        }, 0);
    },

    // + goes to a line without a discount allocation; - empties those lines first so the
    // qualifying unit is removed last. Returns false when the row is not a merged row.
    updateMergedRowQuantity(row, newTotal) {
        const keys = this.getMergedRowKeys(row);
        if (!keys.length || !Array.isArray(this.cart?.items)) return false;
        const lines = keys.map((key) => this.cart.items.find((cartItem) => cartItem.key === key)).filter(Boolean);
        if (!lines.length) return false;

        const hasAllocation = (line) => (line.line_level_discount_allocations || []).length > 0;
        const lineQuantity = (line) => Math.max(0, parseInt(line.quantity, 10) || 0);
        const targets = new Map(lines.map((line) => [line.key, lineQuantity(line)]));
        let delta = Math.max(0, newTotal) - lines.reduce((total, line) => total + lineQuantity(line), 0);
        if (delta > 0) {
            const plusLine = lines.find((line) => !hasAllocation(line)) || lines[lines.length - 1];
            targets.set(plusLine.key, targets.get(plusLine.key) + delta);
        }
        lines.filter((line) => !hasAllocation(line)).concat(lines.filter(hasAllocation)).forEach((line) => {
            if (delta >= 0) return;
            const removed = Math.min(targets.get(line.key), -delta);
            targets.set(line.key, targets.get(line.key) - removed);
            delta += removed;
        });

        const remainingKeys = lines.filter((line) => targets.get(line.key) > 0).map((line) => line.key);
        if (remainingKeys.length) {
            row.dataset.itemKey = remainingKeys[0];
            row.dataset.mergedItemKeys = remainingKeys.join('||');
            row.querySelectorAll('cart-remove-button').forEach((button) => {
                button.dataset.itemKey = remainingKeys[0];
                button.dataset.groupItemKeys = remainingKeys.join('||');
            });
        }
        lines.forEach((line) => {
            const quantity = targets.get(line.key);
            if (quantity === lineQuantity(line)) return;
            this.updateQuantity(this.cart.items.indexOf(line) + 1, quantity, line.key);
        });
        const input = row.querySelector('.quantity__input');
        if (input && remainingKeys.length) input.value = this.getRowDisplayQuantity(row, newTotal);
        return true;
    },

    updateQuantity(lineIndex, newQuantity, itemKey) {
        const mutationVersion = this.beginCartMutation();
        const resolvedItemKey = itemKey || this.cart?.items?.[lineIndex - 1]?.key;
        if (!resolvedItemKey) return;

        // Store the pending update by stable Shopify key. Rapid taps overwrite
        // the same item with the latest desired quantity.
        this.pendingQuantityUpdates.set(resolvedItemKey, { lineIndex, quantity: newQuantity });

        this.applyOptimisticQuantityUpdateByKey(resolvedItemKey, newQuantity);
        if (Number(newQuantity) === 0) {
            this.removeLineItemRowsByKey(resolvedItemKey);
        } else {
            this.updateLineItemUI(lineIndex, newQuantity, resolvedItemKey);
        }
        const packagingUpdates = window.DtGiftPackagingRules?.cartState(this.cart?.items || []).updates || {};
        Object.entries(packagingUpdates).forEach(([key, quantity]) => {
            const packagingLineIndex = (this.cart?.items || []).findIndex(item => item.key === key) + 1;
            this.pendingQuantityUpdates.set(key, { lineIndex: packagingLineIndex, quantity });
            this.applyOptimisticQuantityUpdateByKey(key, quantity);
            if (quantity === 0) this.removeLineItemRowsByKey(key);
            else this.updateLineItemUI(packagingLineIndex, quantity, key);
        });
        this.updateCartCount();
        this.updateEmptyState();
        this.updateOffersUI(this.getEligibleCartValue(this.cart));
        this.dispatchCartUpdated();
        this.forcePricingUpdate();

        // Debounce the actual API call
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.flushQuantityUpdates();
        }, this.DEBOUNCE_MS);
    },

    async flushQuantityUpdates() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.pendingQuantityPromise) {
            return this.pendingQuantityPromise.then((synced) => {
                if (!synced) return false;
                return this.pendingQuantityUpdates.size ? this.flushQuantityUpdates() : true;
            });
        }
        if (this.pendingQuantityUpdates.size === 0) return Promise.resolve(true);

        const mutationVersion = this.mutationVersion;
        const quantityUpdates = Array.from(this.pendingQuantityUpdates.entries());
        // Keep unacknowledged targets available when other cart responses arrive.
        const clearSentUpdates = () => quantityUpdates.forEach(([itemKey, update]) => {
            if (this.pendingQuantityUpdates.get(itemKey) === update) this.pendingQuantityUpdates.delete(itemKey);
        });
        const shouldRefreshCartPage = Boolean(document.querySelector('#main-cart-items'))
            && quantityUpdates.some(([, update]) => update.quantity === 0);

        this.pendingQuantityPromise = (async () => {
            try {
                const updates = quantityUpdates.reduce((acc, [itemKey, update]) => {
                    if (itemKey) acc[itemKey] = Math.max(0, parseInt(update.quantity, 10) || 0);
                    return acc;
                }, {});
                const nextItems = (this.cart?.items || []).map(item => ({
                    ...item,
                    quantity: Object.prototype.hasOwnProperty.call(updates, item.key) ? updates[item.key] : item.quantity
                }));
                Object.assign(updates, window.DtGiftPackagingRules?.cartState(nextItems).updates || {});
                if (!Object.keys(updates).length) return true;

                const doFetch = () =>
                    fetch('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ updates })
                    });
                const withRetry = window.cartAddFetchWithRetry
                    ? (fn) => window.cartAddFetchWithRetry(fn)
                    : (fn) => fn();
                const response = await (window.cartMutationQueue
                    ? window.cartMutationQueue(() => withRetry(doFetch))
                    : withRetry(doFetch));

                if (!response.ok) {
                    throw new Error(`Quantity sync failed with status ${response.status}`);
                }

                const syncedCart = await response.json();
                clearSentUpdates();
                if (!this.isCurrentMutation(mutationVersion)) return true;
                // Shopify can split a line (discount allocation) or re-key it; re-render rows so they regroup.
                const previousKeys = new Set((this.cart?.items || []).map((item) => item.key));
                const hasNewLineKeys = (syncedCart.items || []).some((item) => !previousKeys.has(item.key));
                this.cart = syncedCart;
                this.applyPendingCartUpdates();

                this.updateCartCount();
                this.updateEmptyState();
                await this.forcePricingUpdate();
                this.scheduleCartHydration({
                    refreshRecommendations: shouldRefreshCartPage,
                    refreshCartPage: shouldRefreshCartPage || (hasNewLineKeys && Boolean(document.querySelector('#main-cart-items'))),
                    refreshItems: shouldRefreshCartPage || hasNewLineKeys,
                    mutationVersion
                });
                this.clearError();
                return true;
            } catch (error) {
                console.error('[CartController] Quantity update error:', error);
                clearSentUpdates();
                await this.refreshCart();
                this.updateCartCount();
                this.updateEmptyState();
                this.updateTotals();
                this.updateOffersUI(this.getEligibleCartValue(this.cart));
                await this.refreshCartDrawerHTML({ refreshRecommendations: false });
                await this.refreshCartPageHTML();
                this.showError(window.cartStrings?.error || 'There was an error updating your cart.');
                return false;
            } finally {
                this.pendingQuantityPromise = null;
                if (this.pendingQuantityUpdates.size) {
                    this.debounceTimer = setTimeout(() => {
                        this.flushQuantityUpdates();
                    }, this.DEBOUNCE_MS);
                }
                const drawer = document.querySelector('cart-drawer');
                if (drawer) {
                    drawer.classList.remove('is-pending-add');
                }
                this.hideLoading();
            }
        })();

        return this.pendingQuantityPromise;
    },

    showProductFormError(form, message) {
        if (!form || !message) return;

        let wrapper = form.querySelector('.product-form__error-message-wrapper');
        let errorContainer = form.querySelector('.product-form__error-message');

        if (!wrapper) {
            wrapper = document.createElement('div');
            wrapper.className = 'product-form__error-message-wrapper';
            wrapper.setAttribute('role', 'alert');
            errorContainer = document.createElement('span');
            errorContainer.className = 'product-form__error-message';
            wrapper.appendChild(errorContainer);
            form.appendChild(wrapper);
        }

        if (!errorContainer) {
            errorContainer = document.createElement('span');
            errorContainer.className = 'product-form__error-message';
            wrapper.appendChild(errorContainer);
        }

        errorContainer.textContent = message;
        wrapper.removeAttribute('hidden');
        clearTimeout(wrapper.dtErrorTimer);
        wrapper.dtErrorTimer = setTimeout(() => {
            wrapper.setAttribute('hidden', '');
        }, 5000);
    },

    async rollbackFailedAdd(previousCart, sourceForm, message, mutationVersion) {
        this.removePendingAddedItem();

        try {
            await this.refreshCart();
            if (!this.cart && previousCart) this.cart = previousCart;
            this.updateCartCount();
            this.updateEmptyState();
            this.updateTotals();
            await this.refreshCartDrawerHTML({
                refreshRecommendations: false,
                refreshItems: true,
                mutationVersion
            });
        } catch (error) {
            if (previousCart) {
                this.cart = previousCart;
                this.updateCartCount();
                this.updateEmptyState();
                this.updateTotals();
            }
        }

        this.showProductFormError(sourceForm, message);
    },

    async finishAddToCartRefresh(options = {}) {
        const { refreshCartPage = true } = options;
        try {
            const cartRefresh = this.refreshCart();
            const drawerRefresh = this.refreshCartDrawerHTML({ ...options, waitForCart: cartRefresh });
            await Promise.all([cartRefresh, drawerRefresh]);
            this.updateAllUI();
            if (refreshCartPage) {
                await this.refreshCartPageHTML();
            }
        } catch (error) {
            console.error('[CartController] Add refresh failed:', error);
        }
    },

    scheduleCartHydration(options = {}) {
        if (this.deferredDrawerRefreshTimer) {
            clearTimeout(this.deferredDrawerRefreshTimer);
        }

        const run = () => {
            this.deferredDrawerRefreshTimer = null;
            if (!this.isCurrentMutation(options.mutationVersion)) return;
            this.finishAddToCartRefresh(options);
        };

        this.deferredDrawerRefreshTimer = window.setTimeout(run, 220);
    },

    // Fetch fresh cart drawer HTML from Shopify (with 429 retry for dev server rate limits)
    async refreshCartDrawerHTML(options = {}) {
        const { refreshRecommendations = true, refreshItems = true, mutationVersion, waitForCart, deferRecommendations = false } = options;
        try {
            if (!this.isCurrentMutation(mutationVersion)) return;

            // Use /cart URL for section fetch - ensures cart context is fresh (avoids stale empty cart from /)
            const url = `${window.routes?.cart_url || '/cart'}?section_id=cart-drawer&timestamp=${Date.now()}`;
            const doFetch = () => fetch(url);
            const withRetry = window.cartAddFetchWithRetry
                ? (fn) => window.cartAddFetchWithRetry(fn)
                : (fn) => fn();
            const response = await withRetry(doFetch);
            if (!response.ok) {
                // Section fetch failed (e.g. 429) - still update pricing/offers from cart data
                await this.forcePricingUpdate();
                return;
            }

            const html = await response.text();
            if (waitForCart) await waitForCart;
            if (!this.isCurrentMutation(mutationVersion)) return;

            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const fetchedDrawerRoot = this.getValidCartDrawerRoot(doc);
            const currentDrawerRoot = document.querySelector('#CartDrawer');

            if (!fetchedDrawerRoot || !currentDrawerRoot) {
                await this.forcePricingUpdate();
                return;
            }

            // Update cart drawer items only
            const newItems = fetchedDrawerRoot.querySelector('cart-drawer-items');
            const currentItems = currentDrawerRoot.querySelector('cart-drawer-items');
            const hasPendingRemovalSurface = Boolean(this.pendingRemovalKeys?.size || this.pendingRemovalPromise);
            this.removePendingRemovalRows(newItems);
            this.applyPendingQuantityRows(newItems);
            const newItemsCartRows = newItems?.querySelectorAll?.('.cart-item')?.length ?? 0;
            const expectedCount = this.cart?.item_count ?? 0;
            const hasPendingAddSurface = this.hasPendingAddSurface();
            // Skip replace if fetched HTML has empty cart while the drawer is showing a pending add.
            if ((expectedCount > 0 || hasPendingAddSurface) && newItemsCartRows === 0) {
                await this.forcePricingUpdate();
                return;
            }
            if (refreshItems && newItems && currentItems && !hasPendingRemovalSurface) {
                const currentCoupon = currentItems.querySelector('[data-next-order-coupon]');
                const currentForm = currentItems.querySelector('#CartDrawer-Form');
                const newForm = newItems.querySelector('#CartDrawer-Form');
                if (currentCoupon && currentForm && newForm) {
                    currentForm.replaceWith(newForm);
                } else {
                    currentItems.innerHTML = newItems.innerHTML;
                }
                applyEasyBundleCartRowMeta();
                this.dedupeCompanionRecommendations();
            }
            if (refreshItems && !refreshRecommendations && !hasPendingAddSurface) {
                document.querySelectorAll('.cart_overflow_inner [data-optimistic-merchandising]').forEach(el => el.remove());
            }

            // DON'T update Price Breakdown or Footer HTML - they will be updated by forcePricingUpdate()
            // This prevents the visible lag from showing stale server values before JS updates them

            // Update cart savings progress in the drawer scroll area.
            const newSavingsProgress = fetchedDrawerRoot.querySelector('.cart_overflow_inner [data-cart-savings-progress]');
            const drawerOverflowInner = currentDrawerRoot.querySelector('.cart_overflow_inner');
            replaceCartSavingsProgressBlock(drawerOverflowInner, newSavingsProgress);

            // Handle Offers + Price Breakdown visibility (empty vs non-empty cart)
            const newPriceBreakdown = fetchedDrawerRoot.querySelector('.cart-drawer__price-breakdown');
            const newOffersSection = fetchedDrawerRoot.querySelector('.offers-section');
            const currentPriceBreakdown = currentDrawerRoot.querySelector('.cart-drawer__price-breakdown');
            const currentOffersSection = currentDrawerRoot.querySelector('.offers-section');
            if (newPriceBreakdown && !currentPriceBreakdown) {
                // Cart went from empty to having items - insert offers directly below products, then breakdown.
                const cartItems = currentDrawerRoot.querySelector('cart-drawer-items');
                if (cartItems) {
                    let htmlToInsert = '';
                    if (newOffersSection && !currentOffersSection) htmlToInsert += newOffersSection.outerHTML;
                    htmlToInsert += newPriceBreakdown.outerHTML;
                    cartItems.insertAdjacentHTML('afterend', htmlToInsert);
                }
            } else if (refreshItems && newPriceBreakdown && currentPriceBreakdown) {
                // Keep server-calculated breakdown math in sync after every quantity change.
                currentPriceBreakdown.outerHTML = newPriceBreakdown.outerHTML;
                // Update offers section with fresh unlocked states.
                if (newOffersSection && !currentOffersSection) {
                    document.querySelector('.cart-drawer__price-breakdown')?.insertAdjacentHTML('beforebegin', newOffersSection.outerHTML);
                }
            } else if (!newPriceBreakdown && currentPriceBreakdown) {
                // Cart is now empty - remove breakdown, offers and drawer add-ons
                removeCartDrawerMerchandising();
            }

            if (newOffersSection && currentOffersSection) {
                currentOffersSection.outerHTML = newOffersSection.outerHTML;
            }

            // Update drawer add-ons.
            // Only when cart has items - empty cart content lives in drawer__inner-empty (Liquid-rendered)
            const applyRecommendations = () => {
                if (!this.isCurrentMutation(mutationVersion) || this.cart?.item_count <= 0) return;
                const newPolicySheet = fetchedDrawerRoot.querySelector('.cart_overflow_inner .cart-drawer__policy-section');
                const newWishlistPanel = fetchedDrawerRoot.querySelector('.cart_overflow_inner .cart-page__wishlist-panel');
                const newCustomersBought = fetchedDrawerRoot.querySelector('.cart_overflow_inner .cart-drawer__customers-bought');
                const newAssuranceStrip = fetchedDrawerRoot.querySelector('.cart_overflow_inner .cart-drawer__assurance-strip');
                currentDrawerRoot.querySelectorAll('.cart_overflow_inner [data-optimistic-merchandising], .cart_overflow_inner .cart-drawer__assurance-strip, .cart_overflow_inner .cart-drawer__customers-bought, .cart_overflow_inner .cart-drawer__policy-section, .cart_overflow_inner .cart-drawer__nighttime-essentials, .cart_overflow_inner .cart-page__wishlist-panel, .cart_overflow_inner .cart-drawer-deals-banner').forEach(el => el.remove());
                const cartItemsAnchor = currentDrawerRoot.querySelector('.cart_overflow_inner cart-drawer-items');
                const offersAnchor = currentDrawerRoot.querySelector('.cart_overflow_inner .offers-section');
                const priceBreakdownAnchor = currentDrawerRoot.querySelector('.cart_overflow_inner .cart-drawer__price-breakdown');
                let customersAnchor = offersAnchor || cartItemsAnchor;
                if (newAssuranceStrip && offersAnchor) {
                    offersAnchor.insertAdjacentHTML('afterend', newAssuranceStrip.outerHTML);
                    customersAnchor = offersAnchor.nextElementSibling;
                }
                if (newCustomersBought && customersAnchor) {
                    customersAnchor.insertAdjacentHTML('afterend', newCustomersBought.outerHTML);
                    this.dedupeCompanionRecommendations();
                }
                let addOnAnchor = priceBreakdownAnchor || currentDrawerRoot.querySelector('.cart_overflow_inner .cart-drawer__customers-bought') || offersAnchor || cartItemsAnchor;
                if (newWishlistPanel && addOnAnchor) {
                    addOnAnchor.insertAdjacentHTML('afterend', newWishlistPanel.outerHTML);
                    addOnAnchor = addOnAnchor.nextElementSibling;
                }

                const paymentsAnchor = currentDrawerRoot.querySelector('.cart_overflow_inner .cart-drawer__payments-wrapper');
                if (newPolicySheet && paymentsAnchor) {
                    paymentsAnchor.insertAdjacentHTML('beforebegin', newPolicySheet.outerHTML);
                } else if (newPolicySheet && addOnAnchor) {
                    addOnAnchor.insertAdjacentHTML('afterend', newPolicySheet.outerHTML);
                }
                this.normalizeDrawerSectionOrder();
            };

            if (refreshRecommendations && this.cart?.item_count > 0) {
                if (deferRecommendations) {
                    const schedule = window.requestIdleCallback
                        ? (callback) => window.requestIdleCallback(callback, { timeout: 1200 })
                        : (callback) => window.setTimeout(callback, 0);
                    schedule(applyRecommendations);
                } else {
                    applyRecommendations();
                }
            }

            this.normalizeDrawerSectionOrder();

            // Replace footer totals when cart has items - ensures totals__total-mrp (slashed price) exists
            // (when page loaded empty, footer was rendered without MRP element)
            const newFooter = fetchedDrawerRoot.querySelector('.cart-drawer__footer');
            const currentFooter = currentDrawerRoot.querySelector('.cart-drawer__footer');
            if (refreshItems && newFooter && currentFooter && this.cart?.item_count > 0) {
                currentFooter.outerHTML = newFooter.outerHTML;
            }

            // CRITICAL FIX: After HTML replacement, forcefully update ALL pricing with fresh cart data
            // This ensures prices are correct even if the server-rendered HTML was stale
            await this.forcePricingUpdate();
            applyEasyBundleCartRowMeta();

            // Handle empty state - must hide drawer__inner-empty and show cart items/footer
            const isEmpty = this.cart?.item_count === 0 && !this.hasPendingAddSurface();
            const drawer = document.querySelector('cart-drawer');
            const drawerInner = currentDrawerRoot.querySelector('.drawer__inner');
            const cartDrawerItems = currentDrawerRoot.querySelector('cart-drawer-items');

            if (drawer) {
                drawer.classList.toggle('is-empty', isEmpty);
            }
            if (drawerInner) {
                drawerInner.classList.toggle('is-empty', isEmpty);
            }
            if (cartDrawerItems) {
                cartDrawerItems.classList.toggle('is-empty', isEmpty);
            }

            if (isEmpty) {
                removeCartDrawerMerchandising();
            }

            // Do NOT auto-close when empty - let user browse recommendations and close via back button

            // Notify listeners that cart state has changed (with fresh data)
            if (this.cart) {
                // Dispatch custom event
                document.dispatchEvent(new CustomEvent('cart:updated', { detail: this.cart }));

                // Also publish to legacy pub/sub system for backward compatibility
                if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'cart-controller',
                        cartData: this.cart
                    });
                }
            }

        } catch (error) {
            console.error('[CartController] HTML refresh failed:', error);
        }
    },

    async refreshCartPageHTML() {
        const currentMainItems = document.querySelector('#main-cart-items');
        if (!currentMainItems) {
            this.updateOffersUI(this.getEligibleCartValue(this.cart));
            return;
        }

        try {
            const sectionId = currentMainItems.dataset.id;
            if (!sectionId) {
                this.updateOffersUI(this.getEligibleCartValue(this.cart));
                return;
            }

            const url = `${window.routes?.cart_url || '/cart'}?section_id=${sectionId}&timestamp=${Date.now()}`;
            const doFetch = () => fetch(url);
            const withRetry = window.cartAddFetchWithRetry
                ? (fn) => window.cartAddFetchWithRetry(fn)
                : (fn) => fn();
            const response = await withRetry(doFetch);

            if (!response.ok) {
                this.updateOffersUI(this.getEligibleCartValue(this.cart));
                return;
            }

            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newContents = doc.querySelector('#main-cart-items .js-contents');
            this.removePendingRemovalRows(newContents);
            this.applyPendingQuantityRows(newContents);
            const currentContents = currentMainItems.querySelector('.js-contents');
            const expectedCount = this.cart?.item_count ?? 0;
            const newRows = newContents?.querySelectorAll?.('.cart-item')?.length ?? 0;

            if (expectedCount > 0 && newRows === 0) {
                this.updateOffersUI(this.getEligibleCartValue(this.cart));
                return;
            }

            if (newContents && currentContents) {
                currentContents.innerHTML = newContents.innerHTML;
            }

            const newFooter = doc.querySelector('#main-cart-footer .js-contents-footer');
            const currentFooter = document.querySelector('#main-cart-footer .js-contents-footer');
            if (newFooter && currentFooter) {
                currentFooter.innerHTML = newFooter.innerHTML;
            }

            this.updateOffersUI(this.getEligibleCartValue(this.cart));
        } catch (error) {
            console.error('[CartController] Cart page refresh failed:', error);
            this.updateOffersUI(this.getEligibleCartValue(this.cart));
        }
    },

    dedupeCompanionRecommendations() {
        document.querySelectorAll('[data-cart-companion-nudge][data-nudge-product-handle]').forEach((nudge) => {
            const handle = nudge.dataset.nudgeProductHandle;
            if (!handle) return;

            const section = nudge.parentElement?.querySelector?.('.cart-drawer__customers-bought') ||
                document.querySelector('.cart-drawer__customers-bought');
            if (!section) return;

            section.querySelectorAll('[data-recommendation-product-handle]').forEach((item) => {
                if (item.dataset.recommendationProductHandle === handle) item.remove();
            });

            if (!section.querySelector('[data-recommendation-product-handle]')) section.remove();
        });
    },

    // Force all pricing elements to update with fresh cart data
    // This runs after HTML replacement to fix any stale server-rendered prices
    async forcePricingUpdate(options = {}) {
        if (!this.cart) return;

        this.updateCorporateGiftUI();

        // 1. Update line item prices and handle FREE items
        this.cart.items.forEach((item, index) => {
            const lineIndex = index + 1;
            const row = this.getCartLineRow(item, index);
            if (row?.classList.contains('cart-item--easybundle-parent')) return;

            // Check if this is a free item (final price is 0)
            const isFreeItem = item.final_line_price === 0 && item.original_line_price > 0;

            // Update line item price
            const priceWrapperInline = row?.querySelector('.cart-item__price-wrapper-inline');

            if (priceWrapperInline) {
                if (isFreeItem) {
                    // Show FREE with strikethrough original price
                    priceWrapperInline.innerHTML = `
                        <div class="cart-item__discounted-prices">
                            <span class="price" style="color: rgb(76, 175, 80); font-weight: bold;">FREE</span>
                            <span class="price light-text" style="text-decoration: line-through; color: rgba(0, 0, 0, 0.45); margin-left: 5px; font-size: calc(var(--font-body-scale) * 12px);">
                                ${this.formatMoney(item.original_line_price)}
                            </span>
                        </div>
                    `;
                } else {
                    // Update price normally
                    this.updateLineItemUnitPriceDisplay(row, item);
                }
            }

            // Update quantity
            const quantityInput = document.querySelector(
                `#Drawer-quantity-${lineIndex}, #Quantity-${lineIndex}`
            );
            if (quantityInput) {
                quantityInput.value = this.getRowDisplayQuantity(quantityInput.closest('.cart-item'), item.quantity);
            }
        });

        // 2. Calculate tier discount from selling-price subtotal, NOT MRP
        const tierEligibleValue = this.getEligibleCartValue(this.cart);
        const tierDiscount = this.getTierDiscount(tierEligibleValue);
        const bundlePricingTotals = this.getBundlePricingTotals(this.cart);
        const bundleAwareTotal = bundlePricingTotals.bundleAwareTotal;
        const estimatedYouPay = Math.max(0, bundleAwareTotal - tierDiscount);

        // Update all "You Pay" displays with estimated total (includes tier discount when applicable)
        const totalElements = document.querySelectorAll(
            '.totals__total-value, .cart-drawer__footer .totals__total-value, [data-cart-breakdown-total]'
        );
        totalElements.forEach(el => {
            el.textContent = this.formatMoney(estimatedYouPay);
        });

        // 3. Update subtotals
        const subtotalElements = document.querySelectorAll('[data-cart-subtotal]');
        subtotalElements.forEach(el => {
            el.textContent = this.formatMoney(bundleAwareTotal);
        });

        // 4. Update discounts. EasyBundle's existing line discount is already
        // represented by the fixed bundle price, so do not count it twice.
        const existingBundleDiscount = Math.max(0, bundlePricingTotals.originalBundleTotal - bundlePricingTotals.currentBundleTotal);
        const couponDiscount = this.cart.total_discount || 0;
        const cartDiscount = Math.max(0, couponDiscount - existingBundleDiscount);
        const discountElements = document.querySelectorAll('[data-cart-discount]');
        discountElements.forEach(el => {
            el.textContent = cartDiscount > 0 ? `-${this.formatMoney(cartDiscount)}` : '';
        });

        // 5. Calculate and update MRP + limited-time discount from cart data.
        // NOTE: Shopify cart.js does NOT always include compare_at_price, so preserve server-rendered
        // savings where needed and keep product, bundle, and tier discounts separate.
        let complementaryDiscount = this.calculateComplementaryDiscount(this.cart);
        // Preserve server-rendered complementary discount when cart.js lacks compare_at_price
        const existingComplementary = this.parseMoneyTextToCents(
            document.querySelector('[data-complementary-discount]')?.textContent?.replace(/^-\s*/, '')
        );
        if (complementaryDiscount === 0 && existingComplementary > 0) complementaryDiscount = existingComplementary;

        let bundlePriceAdjustment = Math.max(0, bundlePricingTotals.originalBundleTotal - bundlePricingTotals.fixedBundleTotal);
        const existingRenderedBundleDiscount = this.parseMoneyTextToCents(
            document.querySelector('[data-bundle-discount]')?.textContent?.replace(/^-\s*/, '')
        );
        if (existingRenderedBundleDiscount > bundlePriceAdjustment) bundlePriceAdjustment = existingRenderedBundleDiscount;

        // MRP = pre-discount price = You Pay + limited-time discount + bundle discount + tier offer + coupon
        const mrpTotal = Math.max(
            estimatedYouPay + complementaryDiscount + bundlePriceAdjustment + tierDiscount + cartDiscount,
            estimatedYouPay
        );

        const mrpElements = document.querySelectorAll('[data-cart-mrp]');
        mrpElements.forEach(el => {
            el.textContent = this.formatMoney(mrpTotal);
        });

        // Update complementary discount value (display as "-₹X" to match Liquid output)
        this.ensureDiscountBreakdownRows({ complementaryDiscount, tierDiscount, bundlePriceAdjustment });

        const compDiscountElements = document.querySelectorAll('[data-complementary-discount]');
        compDiscountElements.forEach(el => {
            el.textContent = complementaryDiscount > 0 ? `-${this.formatMoney(complementaryDiscount)}` : '';
        });

        // Show/hide the discount row based on value
        const discountRows = document.querySelectorAll('[data-discount-row]');
        discountRows.forEach((row) => {
            row.style.display = complementaryDiscount > 0 ? '' : 'none';
            if (complementaryDiscount > 0) return;

            const infoIcon = row.querySelector('.price-breakdown__info-icon[aria-controls]');
            if (!infoIcon) return;

            infoIcon.setAttribute('aria-expanded', 'false');
            const infoSection = document.getElementById(infoIcon.getAttribute('aria-controls'));
            if (infoSection) infoSection.hidden = true;
        });

        const bundleDiscountElements = document.querySelectorAll('[data-bundle-discount]');
        bundleDiscountElements.forEach(el => {
            el.textContent = bundlePriceAdjustment > 0 ? `-${this.formatMoney(bundlePriceAdjustment)}` : '';
        });
        const bundleDiscountRows = document.querySelectorAll('[data-bundle-discount-row]');
        bundleDiscountRows.forEach((row) => {
            row.style.display = bundlePriceAdjustment > 0 ? '' : 'none';
        });

        // 7. Update tier discount row (applied at checkout)
        const tierDiscountElements = document.querySelectorAll('[data-tier-discount]');
        tierDiscountElements.forEach(el => {
            el.textContent = tierDiscount > 0 ? `-${this.formatMoney(tierDiscount)}` : '';
        });
        const tierRows = document.querySelectorAll('[data-tier-row]');
        tierRows.forEach((row) => {
            const tierEligibleValueForBadge = this.getEligibleCartValue(this.cart);
            const tierPct = tierEligibleValueForBadge > 0 ? Math.round((tierDiscount / tierEligibleValueForBadge) * 100) : 0;
            const pctBadge = row.querySelector('.price-breakdown__badge');
            if (pctBadge && tierPct > 0) pctBadge.textContent = `${tierPct}%`;
            row.style.display = tierDiscount > 0 ? '' : 'none';
        });

        // 8. Update footer's "Saved" badge and MRP (include tier discount in savings when applicable)
        const totalSavingsForFooter = complementaryDiscount + cartDiscount + tierDiscount + bundlePriceAdjustment;
        const savedBadges = document.querySelectorAll('.totals__saved-badge');
        savedBadges.forEach(badge => {
            badge.textContent = `${this.formatMoney(totalSavingsForFooter)} Saved`;
            badge.style.display = 'inline-block';
        });
        const checkoutSavedElements = document.querySelectorAll('[data-dt-checkout-saved]');
        checkoutSavedElements.forEach(el => {
            el.textContent = `You Saved ${this.formatMoney(totalSavingsForFooter)}!`;
            el.style.display = totalSavingsForFooter > 0 ? '' : 'none';
        });

        // Update footer's strikethrough MRP
        const footerMrpElements = document.querySelectorAll('.totals__total-mrp');
        footerMrpElements.forEach(el => {
            if (totalSavingsForFooter > 0) {
                el.textContent = this.formatMoney(mrpTotal);
                el.style.display = '';
            } else {
                el.style.display = 'none';
            }
        });

        // 9. Update offers section and savings progress - critical when HTML refresh fails during bulk add
        this.updateOffersUI(tierEligibleValue);

        // 10. Trigger discount tier display update
        const discountTiersDisplay = document.querySelector('discount-tiers-display');
        if (options.refreshDiscountTiers !== false && discountTiersDisplay && typeof discountTiersDisplay.fetchCartData === 'function') {
            await discountTiersDisplay.fetchCartData();
        }
    },

    isBundleOfferItem(item) {
        const properties = item?.properties || {};
        const bundleMarkers = ['_dt_bundle_instance', '_easyBundle:OfferId', '_bundleOfferId', '_originalOfferId', '_bundleName', 'Bundle'];
        if (Array.isArray(properties)) {
            return properties.some((property) => {
                if (Array.isArray(property)) return bundleMarkers.includes(property[0]);
                return property && bundleMarkers.includes(property.name);
            });
        }
        return bundleMarkers.some((marker) => Object.prototype.hasOwnProperty.call(properties, marker));
    },

    isRetentionFreebieOfferItem(item, cart = this.cart) {
        if (!item || !cart?.items?.length) return false;
        const hasFreebieOffer = cart.items.some((candidate) => {
            const offerId = this.getItemProperty(candidate, '_easyBundle:OfferId') ||
                this.getItemProperty(candidate, '_bundleOfferId') ||
                this.getItemProperty(candidate, '_originalOfferId');
            return String(offerId || '').split('#')[0].split('_')[0] === 'SDK-896844';
        });
        if (!hasFreebieOffer) return false;
        const productText = `${item.handle || ''} ${item.product_title || ''} ${item.title || ''}`.toLowerCase();
        return productText.includes('freebie') || productText.includes('free gift');
    },

    hasEasyBundleCart(cart = this.cart) {
        if (!cart || !Array.isArray(cart.items)) return false;
        if (cart.attributes?._dt_easybundle_parent_title || cart.attributes?.sbb_box_configurations) return true;

        const easyBundleMarkers = ['_dt_bundle_instance', '_easyBundle:OfferId', '_bundleOfferId', '_originalOfferId', '_bundleName', 'Bundle', 'Box'];
        return cart.items.some((item) => easyBundleMarkers.some((marker) => {
            const value = this.getItemProperty(item, marker);
            return value !== undefined && value !== null && value !== '';
        }));
    },

    getItemProperty(item, propertyName) {
        const properties = item?.properties || {};
        if (Array.isArray(properties)) {
            const entry = properties.find((property) => {
                if (Array.isArray(property)) return property[0] === propertyName;
                return property && property.name === propertyName;
            });
            return Array.isArray(entry) ? entry[1] : entry?.value;
        }
        return properties[propertyName];
    },

    parseBundlePriceToCents(value) {
        const rawValue = String(value || '');
        const priceMatch = rawValue.match(/(?:₹|Rs\.?|INR)\s*([\d,]+(?:\.\d+)?)/i) ||
            rawValue.match(/\bat\s*([\d,]+(?:\.\d+)?)/i);
        if (!priceMatch) return 0;
        const normalized = priceMatch[1].replace(/[^0-9.]/g, '');
        if (!normalized) return 0;
        const amount = parseFloat(normalized);
        return Number.isNaN(amount) ? 0 : Math.round(amount * 100);
    },

    getBundleGroupKey(item) {
        const box = this.getItemProperty(item, 'Box');
        const easyBundleInstance = this.getItemProperty(item, '_dt_bundle_instance');
        const offerId = this.getItemProperty(item, '_easyBundle:OfferId') ||
            this.getItemProperty(item, '_bundleOfferId') ||
            this.getItemProperty(item, '_originalOfferId');

        if (easyBundleInstance) return `EasyBundleInstance-${easyBundleInstance}`;
        if (box) return `EasyBundleBox-${box}`;
        if (offerId) return String(offerId).replace(/_\d+(#.*)?$/, '');
        if (this.getItemProperty(item, '_bundle_key')) return this.getItemProperty(item, '_bundle_key');
        return item.key;
    },

    getBundleDisplayPrice(item) {
        const offerId = this.getItemProperty(item, '_easyBundle:OfferId') ||
            this.getItemProperty(item, '_bundleOfferId') ||
            this.getItemProperty(item, '_originalOfferId');
        const offerMeta = getEasyBundleOfferMetaById(offerId);
        return offerMeta?.title ||
            this.getItemProperty(item, '_bundle_price') ||
            this.getItemProperty(item, '_bundleName') ||
            this.getItemProperty(item, 'Bundle') ||
            this.cart?.attributes?._dt_easybundle_parent_title ||
            '';
    },

    getBundlePricingTotals(cart = this.cart) {
        if (!cart || !Array.isArray(cart.items)) {
            return {
                bundleAwareTotal: 0,
                fixedBundleTotal: 0,
                originalBundleTotal: 0,
                currentBundleTotal: 0
            };
        }

        const bundleGroups = new Map();
        const totals = cart.items.reduce((runningTotals, item) => {
            if (!this.isBundleOfferItem(item)) {
                runningTotals.bundleAwareTotal += Number(item.final_line_price) || 0;
                return runningTotals;
            }

            const bundleKey = this.getBundleGroupKey(item);
            const group = bundleGroups.get(bundleKey) || {
                originalTotal: 0,
                finalTotal: 0,
                fixedPrice: 0
            };
            const quantity = Number(item.quantity) || 1;
            const compareAt = Number(item.selling_plan_allocation?.compare_at_price || item.variant?.compare_at_price || item.compare_at_price || 0);
            const fallbackLine = Number(item.original_line_price) || Number(item.final_line_price) || 0;
            group.originalTotal += compareAt > 0 ? compareAt * quantity : fallbackLine;
            group.finalTotal += Number(item.final_line_price) || 0;
            if (!group.fixedPrice) {
                group.fixedPrice = this.parseBundlePriceToCents(this.getBundleDisplayPrice(item));
            }
            bundleGroups.set(bundleKey, group);
            return runningTotals;
        }, {
            bundleAwareTotal: 0,
            fixedBundleTotal: 0,
            originalBundleTotal: 0,
            currentBundleTotal: 0
        });

        bundleGroups.forEach((group) => {
            const fixedTotal = group.fixedPrice || group.finalTotal;
            totals.bundleAwareTotal += fixedTotal;
            totals.fixedBundleTotal += fixedTotal;
            totals.originalBundleTotal += group.originalTotal;
            totals.currentBundleTotal += group.finalTotal;
        });

        return totals;
    },

    getBundleAwareCartTotal(cart = this.cart) {
        return this.getBundlePricingTotals(cart).bundleAwareTotal;
    },

    getEligibleCartValue(cart = this.cart) {
        if (!cart || !Array.isArray(cart.items)) return 0;
        const eligibleItems = cart.items.filter((item) => {
            const offerId = this.getItemProperty(item, '_easyBundle:OfferId') ||
                this.getItemProperty(item, '_bundleOfferId') ||
                this.getItemProperty(item, '_originalOfferId');
            const isRetentionOffer = String(offerId || '').split('#')[0].split('_')[0] === 'SDK-896844';
            return !(
                isRetentionOffer ||
                this.isRetentionFreebieOfferItem(item, cart) ||
                this.getItemProperty(item, '_dt_retention_bundle') ||
                this.getItemProperty(item, '_dt_retention_gift')
            );
        });
        return this.getBundlePricingTotals({ ...cart, items: eligibleItems }).bundleAwareTotal;
    },

    formatIndianRupees(amountInRupees) {
        const amount = Math.max(0, Math.round(amountInRupees || 0));
        return `\u20B9${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    },

    getSavingsOfferState(cartValueInCents = this.getEligibleCartValue()) {
        const cartValue = Math.max(0, Math.round((cartValueInCents || 0) / 100));
        const maxThreshold = CART_VALUE_OFFERS[CART_VALUE_OFFERS.length - 1].threshold;
        let currentUnlockedOffer = null;
        let nextOffer = null;

        CART_VALUE_OFFERS.forEach((offer) => {
            if (cartValue >= offer.threshold) {
                currentUnlockedOffer = offer;
            } else if (!nextOffer) {
                nextOffer = offer;
            }
        });

        const remainingAmount = nextOffer ? Math.max(nextOffer.threshold - cartValue, 0) : 0;
        const segmentWidth = 100 / Math.max(CART_VALUE_OFFERS.length - 1, 1);
        let progressPercent = cartValue >= maxThreshold ? 100 : 0;
        if (cartValue >= CART_VALUE_OFFERS[0].threshold && cartValue < maxThreshold) {
            for (let index = 1; index < CART_VALUE_OFFERS.length; index += 1) {
                const start = CART_VALUE_OFFERS[index - 1].threshold;
                const end = CART_VALUE_OFFERS[index].threshold;
                const segmentStart = (index - 1) * segmentWidth;
                if (cartValue < end) {
                    const segmentProgress = (cartValue - start) / (end - start);
                    progressPercent = segmentStart + Math.max(0, Math.min(segmentProgress, 1)) * segmentWidth;
                    break;
                }
            }
        }
        let message = '';

        if (!currentUnlockedOffer && nextOffer) {
            message = `Add ${this.formatIndianRupees(remainingAmount)} more to unlock <strong class="cart-savings-progress__offer">${nextOffer.discountLabel}</strong>`;
        } else if (currentUnlockedOffer && nextOffer) {
            message = `Add ${this.formatIndianRupees(remainingAmount)} more to get <strong class="cart-savings-progress__offer">${nextOffer.discountLabel}</strong>`;
        } else {
            message = 'Maximum cart offer reached';
        }

        return {
            cartValue,
            currentUnlockedOffer,
            nextOffer,
            remainingAmount,
            progressPercent,
            message,
            ariaLabel: nextOffer
                ? `Cart value ${this.formatIndianRupees(cartValue)}. Add ${this.formatIndianRupees(remainingAmount)} more to unlock ${nextOffer.discountLabel}.`
                : `Cart value ${this.formatIndianRupees(cartValue)}. Maximum cart offer reached.`
        };
    },

    updateSavingsProgressUI(cartValueInCents = this.getEligibleCartValue()) {
        const progressCards = document.querySelectorAll('[data-cart-savings-progress]');
        if (!progressCards.length) return;

        const hasPendingAddSurface = this.hasPendingAddSurface();
        let effectiveCartValue = cartValueInCents;
        if (hasPendingAddSurface && effectiveCartValue <= 0) {
            const previousCartValue = Array.from(progressCards).reduce((maxValue, card) => {
                const value = parseInt(card.dataset.cartValue || '0', 10);
                return Number.isFinite(value) ? Math.max(maxValue, value) : maxValue;
            }, 0);
            if (previousCartValue > 0) effectiveCartValue = previousCartValue;
        }

        const state = this.getSavingsOfferState(effectiveCartValue);
        const isEmpty = this.cart ? this.cart.item_count === 0 && !hasPendingAddSurface : false;

        progressCards.forEach((card) => {
            card.hidden = isEmpty;
            card.dataset.cartValue = String(effectiveCartValue || 0);
            const hasSavingsGlow = effectiveCartValue >= CART_VALUE_OFFERS[0].threshold * 100;
            card.classList.toggle('has-cart-savings-glow', hasSavingsGlow);
            card.closest('cart-drawer')?.classList.toggle('has-cart-savings-glow', hasSavingsGlow);
            card.closest('#CartDrawer')?.classList.toggle('has-cart-savings-glow', hasSavingsGlow);
            card.closest('.drawer__inner')?.classList.toggle('has-cart-savings-glow', hasSavingsGlow);

            const valueEl = card.querySelector('[data-cart-savings-value]');
            if (valueEl) valueEl.textContent = `${this.formatIndianRupees(state.cartValue)} in cart`;

            const shouldShowIntroMessage = card.classList.contains('is-cart-savings-intro-pending') ||
                card.classList.contains('is-cart-savings-intro-playing') ||
                card.classList.contains('is-cart-savings-message-intro-visible');
            const isMessageTransitioning = card.classList.contains('is-cart-savings-message-entering') ||
                card.classList.contains('is-cart-savings-message-switching');
            const introMessage = state.currentUnlockedOffer
                ? `Congratulations! You've unlocked <strong class="cart-savings-progress__offer">${state.currentUnlockedOffer.shortLabel}</strong> Discount!`
                : state.message;

            card.querySelectorAll('[data-cart-savings-message]').forEach((messageEl) => {
                messageEl.dataset.cartSavingsDefaultHtml = state.message;
                if (isMessageTransitioning) return;

                messageEl.innerHTML = shouldShowIntroMessage ? introMessage : state.message;
                messageEl.querySelectorAll('strong').forEach((strong) => {
                    strong.classList.add('cart-savings-progress__offer');
                    strong.style.color = '#0e8635';
                    strong.style.fontWeight = '700';
                });
            });

            card.querySelectorAll('[data-cart-savings-fill]').forEach((fillEl) => {
                fillEl.style.setProperty('--cart-savings-fill-scale', String(Math.max(0, Math.min(state.progressPercent / 100, 1))));
            });

            card.querySelectorAll('[data-cart-savings-segment-fill]').forEach((segmentFill) => {
                const segmentIndex = parseInt(segmentFill.dataset.segmentIndex || '0', 10);
                const segmentStart = segmentIndex * (100 / Math.max(CART_VALUE_OFFERS.length - 1, 1));
                const segmentWidth = 100 / Math.max(CART_VALUE_OFFERS.length - 1, 1);
                const segmentScale = Math.max(0, Math.min((state.progressPercent - segmentStart) / segmentWidth, 1));
                segmentFill.style.setProperty('--segment-fill-scale', String(segmentScale));
            });

            const barEl = card.querySelector('[data-cart-savings-bar]');
            if (barEl) {
                barEl.setAttribute('aria-valuenow', String(Math.min(state.cartValue, CART_VALUE_OFFERS[CART_VALUE_OFFERS.length - 1].threshold)));
                barEl.setAttribute('aria-label', state.ariaLabel);
            }

            card.querySelectorAll('[data-cart-savings-marker]').forEach((marker) => {
                const threshold = parseInt(marker.dataset.threshold || '0', 10);
                const isUnlocked = effectiveCartValue >= threshold;
                const offerIndex = CART_VALUE_OFFERS.findIndex((offer) => offer.threshold * 100 === threshold);
                const previousThreshold = offerIndex > 0 ? CART_VALUE_OFFERS[offerIndex - 1].threshold * 100 : 0;
                const milestoneRange = Math.max(threshold - previousThreshold, 1);
                const milestoneScale = threshold === CART_VALUE_OFFERS[0].threshold * 100
                    ? (isUnlocked ? 1 : 0)
                    : Math.max(0, Math.min((effectiveCartValue - previousThreshold) / milestoneRange, 1));
                marker.classList.toggle('is-unlocked', isUnlocked);
                marker.classList.remove('is-current');
                marker.style.setProperty('--milestone-fill', `${(milestoneScale * 100).toFixed(2)}%`);
            });

            card.querySelectorAll('[data-cart-savings-percent]').forEach((percent) => {
                const threshold = parseInt(percent.dataset.threshold || '0', 10);
                const isUnlocked = effectiveCartValue >= threshold;
                percent.classList.toggle('is-unlocked', isUnlocked);
                percent.classList.remove('is-current');
            });

            card.querySelectorAll('[data-cart-savings-label]').forEach((label) => {
                const threshold = parseInt(label.dataset.threshold || '0', 10);
                label.classList.toggle('is-unlocked', effectiveCartValue >= threshold);
            });
        });

        if (window.CartSavingsProgressCollapse) {
            refreshCartSavingsProgressWhenDrawerIsReady();
        }

        this.applyOffersCouponState(effectiveCartValue);
    },

    applyOffersCouponState(cartValueInCents = this.getEligibleCartValue()) {
        const hair350Eligible = (this.cart?.items || []).some(item =>
            item.quantity > 0 && [7880495300821, 8170258137301].includes(Number(item.product_id))
        );
        document.querySelectorAll('cart-hair350-offer .hair350-ticket').forEach(card => {
            card.classList.toggle('offers-coupon-card--active', hair350Eligible);
        });

        document.querySelectorAll('.offers-section[data-offers-ready="false"]').forEach((section) => {
            section.dataset.offersReady = 'true';
        });

        const nextOrderCards = document.querySelectorAll('.offers-section [data-next-order-offer]');
        const activeNextOrderOffer = cartValueInCents >= 249900
            ? 'next1000'
            : cartValueInCents >= 49900
                ? 'next500'
                : '';

        nextOrderCards.forEach((card) => {
            card.classList.toggle(
                'offers-coupon-card--active',
                card.dataset.nextOrderOffer === activeNextOrderOffer
            );
        });

        const cards = document.querySelectorAll('.offers-section [data-offer]:not([data-next-order-offer])');
        if (!cards.length) return;

        const thresholdsByOffer = CART_VALUE_OFFERS.reduce((map, offer) => {
            map[offer.offerKey] = offer.threshold * 100;
            return map;
        }, {});
        const activeOffer = CART_VALUE_OFFERS.reduce((current, offer) => (
            cartValueInCents >= offer.threshold * 100 ? offer : current
        ), null);
        const activeOfferKey = activeOffer?.offerKey || '';
        const activeThreshold = activeOffer ? activeOffer.threshold * 100 : 0;
        const visibleOfferCount = activeOffer
            ? CART_VALUE_OFFERS.filter((offer) => offer.threshold * 100 >= activeThreshold).length
            : CART_VALUE_OFFERS.length;

        cards.forEach((card) => {
            const offer = (card.dataset.offer || '').toLowerCase();
            const threshold = thresholdsByOffer[offer];
            const isActive = offer === activeOfferKey;
            const shouldHide = Boolean(activeThreshold && threshold && threshold < activeThreshold);
            card.hidden = shouldHide;
            card.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
            card.classList.toggle('offers-coupon-card--active', isActive);
        });

        const nextOrderOfferCount = document.querySelectorAll('.offers-section [data-next-order-offer]').length;
        const totalVisibleOfferCount = visibleOfferCount + nextOrderOfferCount;

        document.querySelectorAll('.pdp-offers-summary__count').forEach((countEl) => {
            countEl.textContent = `${totalVisibleOfferCount} offer${totalVisibleOfferCount === 1 ? '' : 's'} available`;
        });
    },

    // Update offer card unlocked state from cart data.
    updateOffersUI(cartValueInCents = this.getEligibleCartValue()) {
        this.updateSavingsProgressUI(cartValueInCents);
        this.applyOffersCouponState(cartValueInCents);
    },

    // ============================================
    // REMOVE ITEM
    // ============================================
    async removeItemsByKeys(itemKeys) {
        const keys = Array.isArray(itemKeys) ? itemKeys.filter(Boolean) : [];
        if (!keys.length) return;

        const mutationVersion = this.beginCartMutation();
        const groupedRows = new Set();
        document.querySelectorAll('cart-remove-button[data-group-item-keys]').forEach((button) => {
            const groupItemKeys = button.dataset.groupItemKeys || '';
            if (keys.some((key) => groupItemKeys.includes(key))) {
                const row = button.closest('.cart-item');
                if (row) groupedRows.add(row);
            }
        });

        this.clearError();
        keys.forEach((key) => this.pendingQuantityUpdates.delete(key));
        if (!this.pendingQuantityUpdates.size && this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        keys.forEach((key) => this.removeLineItemRowsByKey(key));
        groupedRows.forEach((row) => row.remove());
        keys.forEach((key) => {
            this.pendingRemovalKeys.add(key);
            this.applyOptimisticRemoveByKey(key);
        });
        this.updateCartCount();
        this.updateEmptyState();
        this.updateTotals();
        this.forcePricingUpdate({ refreshDiscountTiers: false }).catch((error) => console.error('[CartController] Optimistic pricing update failed:', error));
        this.updateOffersUI(this.getEligibleCartValue(this.cart));
        this.scheduleRemovalSync(mutationVersion);
        this.clearError();
    },

    getBundleItemKeys(bundleKey) {
        if (!bundleKey || !Array.isArray(this.cart?.items)) return [];

        return this.cart.items.reduce((keys, item) => {
            if (!item?.key || !this.isBundleOfferItem(item)) return keys;
            if (this.getBundleGroupKey(item) === bundleKey || this.getItemProperty(item, '_bundle_key') === bundleKey) {
                keys.push(item.key);
            }
            return keys;
        }, []);
    },

    async removeBundle(bundleKey) {
        if (!bundleKey) return;

        const bundleItemKeys = this.getBundleItemKeys(bundleKey);
        if (bundleItemKeys.length) {
            await this.removeItemsByKeys(bundleItemKeys);
            return;
        }

        const mutationVersion = this.beginCartMutation();
        this.clearError();

        if (this.isProcessing) {
            await this.waitForProcessing();
        }

        this.isProcessing = true;
        this.showLoading();

        try {
            const updates = {};
            (this.cart?.items || []).forEach((item) => {
                if (this.isBundleOfferItem(item) && this.getBundleGroupKey(item) === bundleKey && item.key) {
                    updates[item.key] = 0;
                }
            });

            if (!Object.keys(updates).length) {
                const escapedBundleKey = window.CSS && typeof window.CSS.escape === 'function'
                    ? window.CSS.escape(bundleKey)
                    : String(bundleKey).replace(/"/g, '\\"');
                document.querySelectorAll(`cart-remove-button[data-bundle-key="${escapedBundleKey}"]`).forEach((button) => {
                    const lineIndex = parseInt(button.dataset.index, 10);
                    if (!Number.isNaN(lineIndex) && this.cart?.items?.[lineIndex - 1]?.key) {
                        updates[this.cart.items[lineIndex - 1].key] = 0;
                    }
                });
            }

            Object.keys(updates).forEach((key) => this.pendingQuantityUpdates.delete(key));
            if (!this.pendingQuantityUpdates.size && this.debounceTimer) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = null;
            }

            const doFetch = () =>
                fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });
            const withRetry = window.cartAddFetchWithRetry
                ? (fn) => window.cartAddFetchWithRetry(fn)
                : (fn) => fn();
            const response = await (window.cartMutationQueue
                ? window.cartMutationQueue(() => withRetry(doFetch))
                : withRetry(doFetch));

            if (!response.ok) {
                throw new Error(`Bundle remove failed with status ${response.status}`);
            }

            const syncedCart = await response.json();
            if (!this.isCurrentMutation(mutationVersion)) return;
            this.cart = syncedCart;
            this.applyPendingCartUpdates();
            this.updateCartCount();
            this.updateEmptyState();
            this.updateTotals();
            this.updateOffersUI(this.getEligibleCartValue(this.cart));
            this.scheduleCartHydration({
                refreshRecommendations: true,
                refreshCartPage: Boolean(document.querySelector('#main-cart-items')),
                refreshItems: true,
                mutationVersion
            });
            this.clearError();
        } catch (error) {
            console.error('[CartController] Bundle remove error:', error);
            await this.refreshCart();
            this.updateCartCount();
            this.updateEmptyState();
            this.updateTotals();
            this.updateOffersUI(this.getEligibleCartValue(this.cart));
            this.showError(window.cartStrings?.error || 'There was an error updating your cart.');
            this.scheduleCartHydration({
                refreshRecommendations: false,
                refreshCartPage: Boolean(document.querySelector('#main-cart-items')),
                refreshItems: true,
                mutationVersion
            });
        } finally {
            const drawer = document.querySelector('cart-drawer');
            if (drawer) {
                drawer.classList.remove('is-pending-add');
            }
            this.isProcessing = false;
            this.hideLoading();
        }
    },

    async remove(lineIndex, itemKey) {
        const mutationVersion = this.beginCartMutation();
        const lineItem = document.querySelector(`#CartDrawer-Item-${lineIndex}, #CartItem-${lineIndex}`);
        const rowItemKey = lineItem?.dataset?.itemKey;
        const requestedItemKey = itemKey || rowItemKey || this.cart?.items?.[lineIndex - 1]?.key;
        const removedItemIndex = this.cart?.items?.findIndex((item) => item.key === requestedItemKey) ?? -1;
        const removedItem = removedItemIndex >= 0 ? this.cart.items[removedItemIndex] : this.cart?.items?.[lineIndex - 1];
        const removedItemKey = removedItem?.key || requestedItemKey;
        const didOptimisticRemove = Boolean(lineItem || removedItem);

        if (removedItem?.key) {
            if (!this.pendingRemovalRollbacks) this.pendingRemovalRollbacks = new Map();
            if (!this.pendingRemovalRollbacks.has(removedItem.key)) {
                this.pendingRemovalRollbacks.set(removedItem.key, {
                    item: { ...removedItem },
                    index: removedItemIndex,
                    row: lineItem,
                    parent: lineItem?.parentNode,
                    next: lineItem?.nextSibling
                });
            }
        }

        this.clearError();
        this.pendingQuantityUpdates.delete(removedItemKey);
        if (!this.pendingQuantityUpdates.size && this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        if (lineItem) {
            lineItem.remove();
        }
        if (removedItem?.key) {
            this.pendingRemovalKeys.add(removedItem.key);
        }

        if (removedItem) {
            this.applyOptimisticRemoveByKey(removedItem.key || removedItemKey);
            this.updateCartCount();
            this.updateEmptyState();
            this.updateTotals();
            this.forcePricingUpdate({ refreshDiscountTiers: false }).catch((error) => console.error('[CartController] Optimistic pricing update failed:', error));
            this.updateOffersUI(this.getEligibleCartValue(this.cart));
            this.dispatchCartUpdated();
        }

        if (didOptimisticRemove) {
            this.scheduleRemovalSync(mutationVersion);
            this.clearError();
        } else if (removedItemKey) {
            this.pendingRemovalKeys.add(removedItemKey);
            this.scheduleRemovalSync(mutationVersion);
        }
    },

    scheduleRemovalSync(mutationVersion) {
        if (this.pendingRemovalTimer) {
            clearTimeout(this.pendingRemovalTimer);
        }

        this.pendingRemovalTimer = setTimeout(() => {
            this.pendingRemovalTimer = null;
            this.flushPendingRemovals(mutationVersion);
        }, 280);
    },

    async flushPendingRemovals(mutationVersion = this.mutationVersion) {
        if (this.pendingRemovalTimer) {
            clearTimeout(this.pendingRemovalTimer);
            this.pendingRemovalTimer = null;
        }
        if (this.pendingRemovalPromise) {
            return this.pendingRemovalPromise.then((synced) => {
                if (!synced) return false;
                return this.pendingRemovalKeys.size ? this.flushPendingRemovals(this.mutationVersion) : true;
            });
        }
        if (!this.pendingRemovalKeys.size) return Promise.resolve(true);

        const keysToRemove = Array.from(this.pendingRemovalKeys);
        const updates = keysToRemove.reduce((acc, key) => {
            if (key) acc[key] = 0;
            return acc;
        }, {});

        this.pendingRemovalPromise = (async () => {
            try {
                if (!Object.keys(updates).length) return true;

                const doFetch = () =>
                    fetch('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ updates })
                    });
                const withRetry = window.cartAddFetchWithRetry
                    ? (fn) => window.cartAddFetchWithRetry(fn)
                    : (fn) => fn();
                const response = await (window.cartMutationQueue
                    ? window.cartMutationQueue(() => withRetry(doFetch))
                    : withRetry(doFetch));

                if (!response.ok) {
                    throw new Error(`Remove sync failed with status ${response.status}`);
                }

                const syncedCart = await response.json();
                keysToRemove.forEach((key) => {
                    this.pendingRemovalKeys.delete(key);
                    this.pendingRemovalRollbacks?.delete(key);
                });
                if (!this.isCurrentMutation(mutationVersion)) return true;

                this.cart = syncedCart;
                this.applyPendingCartUpdates();
                this.updateCartCount();
                this.updateEmptyState();
                this.updateTotals();
                await this.forcePricingUpdate({ refreshDiscountTiers: false });
                this.updateOffersUI(this.getEligibleCartValue(this.cart));
                this.scheduleCartHydration({
                    refreshRecommendations: true,
                    refreshCartPage: Boolean(document.querySelector('#main-cart-items')),
                    refreshItems: this.cart?.item_count > 0,
                    mutationVersion
                });
                this.clearError();
                return true;
            } catch (error) {
                console.error('[CartController] Remove sync error:', error);
                // A rejected removal is no longer pending. Restore it before reading
                // the cart so a blocked request cannot leave an invisible item or retry forever.
                keysToRemove.slice().reverse().forEach((key) => {
                    this.pendingRemovalKeys.delete(key);
                    this.restoreFailedRemoval(key);
                });
                this.applyPendingCartUpdates();
                await this.refreshCart();
                keysToRemove.forEach((key) => {
                    if (!this.cart?.items.some((item) => item.key === key)) this.removeLineItemRowsByKey(key);
                });
                this.updateCartCount();
                this.updateEmptyState();
                this.updateTotals();
                await this.forcePricingUpdate({ refreshDiscountTiers: false });
                this.updateOffersUI(this.getEligibleCartValue(this.cart));
                this.scheduleCartHydration({
                    refreshRecommendations: false,
                    refreshCartPage: Boolean(document.querySelector('#main-cart-items')),
                    mutationVersion
                });
                this.showError(window.cartStrings?.error || 'There was an error updating your cart.');
                return false;
            } finally {
                this.pendingRemovalPromise = null;
                if (this.pendingRemovalKeys.size) {
                    this.scheduleRemovalSync(this.mutationVersion);
                }
            }
        })();

        return this.pendingRemovalPromise;
    },

    restoreFailedRemoval(itemKey) {
        const rollback = this.pendingRemovalRollbacks?.get(itemKey);
        if (!rollback) return;
        this.pendingRemovalRollbacks.delete(itemKey);
        const { item, index, row, parent, next } = rollback;
        if (this.cart && !this.cart.items.some((cartItem) => cartItem.key === itemKey)) {
            this.cart.items.splice(Math.max(0, index), 0, item);
            this.cart.item_count += Number(item.quantity) || 0;
            const linePrice = Number(item.final_line_price ?? item.final_price * item.quantity) || 0;
            this.cart.items_subtotal_price += linePrice;
            this.cart.total_price += linePrice;
        }
        if (row && !row.isConnected && parent?.isConnected) {
            const itemIndex = this.cart?.items.findIndex((cartItem) => cartItem.key === itemKey) ?? index;
            const nextRow = next?.parentNode === parent ? next : Array.from(parent.children).find((element) =>
                this.cart?.items.findIndex((cartItem) => cartItem.key === element.dataset.itemKey) > itemIndex
            );
            parent.insertBefore(row, nextRow || null);
        }
    },

    cloneCartData(cart) {
        if (!cart) return null;
        try {
            return JSON.parse(JSON.stringify(cart));
        } catch (_) {
            return null;
        }
    },

    applyOptimisticRemove(lineIndex) {
        if (!this.cart?.items?.[lineIndex - 1]) return;
        const item = this.cart.items[lineIndex - 1];
        this.applyOptimisticRemoveByKey(item.key);
    },

    applyOptimisticRemoveByKey(itemKey) {
        if (!itemKey || !this.cart?.items) return;
        const itemIndex = this.cart.items.findIndex((item) => item.key === itemKey);
        if (itemIndex < 0) return;
        const item = this.cart.items[itemIndex];
        const quantity = Number(item.quantity) || 0;
        const linePrice = Number(item.final_line_price || item.final_price * quantity || 0);

        this.cart.items.splice(itemIndex, 1);
        this.cart.item_count = Math.max(0, (Number(this.cart.item_count) || 0) - quantity);
        this.cart.items_subtotal_price = Math.max(0, (Number(this.cart.items_subtotal_price) || 0) - linePrice);
        this.cart.total_price = Math.max(0, (Number(this.cart.total_price) || 0) - linePrice);
    },

    // ============================================
    // UI UPDATES (Targeted, not full replacement)
    // ============================================
    updateAllUI() {
        if (!this.cart) return;

        this.updateCartCount();
        this.updateTotals();
        // updateSavingsBadge() removed - now handled by forcePricingUpdate()
        this.updateEmptyState();
        this.updateLineItemPrices();
        this.updateOffersUI(this.getEligibleCartValue(this.cart));
    },

    updateLineItemUI(lineIndex, quantity, itemKey) {
        // Update the quantity input immediately
        const escapedKey = itemKey && window.CSS && typeof window.CSS.escape === 'function'
            ? window.CSS.escape(itemKey)
            : itemKey ? String(itemKey).replace(/"/g, '\\"') : '';
        const keyedRow = escapedKey ? document.querySelector(`[data-item-key="${escapedKey}"]`) : null;
        const input = keyedRow?.querySelector('.quantity__input') || document.querySelector(
            `#Drawer-quantity-${lineIndex}, #Quantity-${lineIndex}`
        );
        const displayQuantity = keyedRow ? this.getRowDisplayQuantity(keyedRow, quantity) : quantity;
        if (input && parseInt(input.value) !== displayQuantity) {
            input.value = displayQuantity;
        }

        // Calculate and update line price (optimistic)
        const item = itemKey && this.cart?.items
            ? this.cart.items.find((cartItem) => cartItem.key === itemKey)
            : this.cart?.items?.[lineIndex - 1];
        if (item) {
            const priceElement = keyedRow?.querySelector('.cart-item__price-wrapper-inline .price, .cart-item__price-wrapper .price') || document.querySelector(
                `#CartDrawer-Item-${lineIndex} .cart-item__price-wrapper-inline .price, #CartItem-${lineIndex} .price`
            );
            if (keyedRow) this.updateLineItemUnitPriceDisplay(keyedRow, item);
            else if (priceElement) priceElement.textContent = this.formatMoney(this.getItemUnitPrice(item));
        }
    },

    removeLineItemRowsByKey(itemKey) {
        if (!itemKey) return;
        const escapedKey = window.CSS && typeof window.CSS.escape === 'function'
            ? window.CSS.escape(itemKey)
            : String(itemKey).replace(/"/g, '\\"');
        document.querySelectorAll(`[data-item-key="${escapedKey}"]`).forEach((row) => {
            row.remove();
        });
    },

    updateLineItemPrices() {
        if (!this.cart?.items) return;

        this.cart.items.forEach((item, index) => {
            const lineIndex = index + 1;
            const lineItem = this.getCartLineRow(item, index);
            if (!lineItem) return;

            // Update quantity input
            const input = lineItem.querySelector('.quantity__input');
            if (input) {
                input.value = this.getRowDisplayQuantity(lineItem, item.quantity);
                input.dataset.cartQuantity = item.quantity;
            }

            // Update price
            this.updateLineItemUnitPriceDisplay(lineItem, item);

            // Restore visibility (in case it was hidden during remove)
            lineItem.style.opacity = '';
            lineItem.style.pointerEvents = '';
        });

        // Remove deleted items from DOM (items that are no longer in cart)
        const allLineItems = document.querySelectorAll('[id^="CartDrawer-Item-"], [id^="CartItem-"]');
        allLineItems.forEach(el => {
            const match = el.id.match(/(?:CartDrawer-Item-|CartItem-)(\d+)/);
            if (match) {
                const lineIndex = parseInt(match[1]);
                if (lineIndex > this.cart.items.length) {
                    el.remove();
                }
            }
        });
    },

    updateCartCount() {
        const items = this.cart?.items;
        const count = Array.isArray(items)
            ? items.reduce((total, item) => total + (item.properties?._corporateGiftset ? 0 : item.quantity), 0)
                + (items.some(item => item.properties?._corporateGiftset) ? 1 : 0)
            : this.cart?.item_count || 0;

        // Header cart bubble
        const bubbles = document.querySelectorAll('.cart-count-bubble');
        bubbles.forEach(bubble => {
            const countSpan = bubble.querySelector('[aria-hidden="true"]') || bubble;
            if (countSpan) {
                countSpan.textContent = count;
            }
            const label = bubble.querySelector('.visually-hidden');
            if (label) {
                label.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
            }
            bubble.classList.toggle('hidden', count === 0);
        });

        // Also try data attribute selectors
        document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = count;
        });
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (cartLink) cartLink.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    },

    updateCorporateGiftUI() {
        const corporateItems = new Map((this.cart.items || [])
            .filter(item => item.properties?._corporateGiftset)
            .map(item => [item.key, item]));
        const corporateQuantity = [...corporateItems.values()].reduce((sum, item) => sum + item.quantity, 0);
        const corporateSubtotal = [...corporateItems.values()].reduce((sum, item) => sum + item.final_line_price, 0);
        document.querySelectorAll('.dt-corporate-group').forEach(group => {
            if (!corporateQuantity) {
                group.closest('.dt-corporate-group-row')?.remove();
                return;
            }
            const quantityLabel = group.querySelector('[data-corporate-quantity]');
            if (quantityLabel) quantityLabel.textContent = corporateQuantity;
            group.querySelector('.dt-corporate-group__subtotal').textContent = this.formatMoney(corporateSubtotal);
            group.querySelectorAll('.dt-corporate-group__item').forEach(row => {
                const item = corporateItems.get(row.dataset.itemKey);
                if (item) row.querySelector('.dt-corporate-group__line-price').textContent = this.formatMoney(item.final_line_price);
            });
        });
    },

    updateTotals() {
        if (!this.cart) return;

        this.updateCorporateGiftUI();

        const tierEligibleValue = this.getEligibleCartValue(this.cart);
        const tierDiscount = this.getTierDiscount(tierEligibleValue);
        const bundlePricingTotals = this.getBundlePricingTotals(this.cart);
        const bundleAwareTotal = bundlePricingTotals.bundleAwareTotal;
        const estimatedYouPay = Math.max(0, bundleAwareTotal - tierDiscount);

        // Update sticky footer total with the same checkout estimate as Price Breakdown.
        const totalElements = document.querySelectorAll('.totals__total-value');
        totalElements.forEach(el => {
            el.textContent = this.formatMoney(estimatedYouPay);
        });

        // Update MRP (original price before discounts)
        const totalDiscount = this.calculateTotalDiscount() + tierDiscount;
        const mrpElements = document.querySelectorAll('.totals__total-mrp');
        mrpElements.forEach(el => {
            if (totalDiscount > 0) {
                el.textContent = this.formatMoney(estimatedYouPay + totalDiscount);
                el.style.display = 'inline-block';
            } else {
                el.style.display = 'none';
            }
        });
    },

    getTierDiscount(cartValueInCents = this.getEligibleCartValue(this.cart)) {
        let tierPct = 0;
        if (cartValueInCents >= 2000000) tierPct = 20;
        else if (cartValueInCents >= 1000000) tierPct = 15;
        else if (cartValueInCents >= 500000) tierPct = 10;
        else if (cartValueInCents >= 300000) tierPct = 5;

        return tierPct > 0 ? Math.round(cartValueInCents * tierPct / 100) : 0;
    },

    getItemUnitPrice(item) {
        const quantity = Math.max(1, Number(item?.quantity) || 1);
        return Number(item?.final_price || item?.price || (item?.final_line_price ? Math.round(item.final_line_price / quantity) : 0) || 0);
    },

    getItemUnitMrp(item, row = null) {
        const quantity = Math.max(1, Number(item?.quantity) || 1);
        const saleUnit = this.getItemUnitPrice(item);
        const dataUnitMrp = Number(row?.dataset?.lineUnitMrp || 0);
        if (dataUnitMrp > saleUnit) return dataUnitMrp;

        const compareAt = Number(item?.selling_plan_allocation?.compare_at_price || item?.variant?.compare_at_price || item?.compare_at_price || 0);
        if (compareAt > saleUnit) return compareAt;

        const renderedMrp = this.parseMoneyTextToCents(row?.querySelector('[data-line-item-mrp], .cart-item__old-price')?.textContent);
        if (renderedMrp > saleUnit) {
            if (renderedMrp > saleUnit * quantity) return Math.round(renderedMrp / quantity);
            return renderedMrp;
        }

        const originalUnit = Number(item?.original_price || (item?.original_line_price ? Math.round(item.original_line_price / quantity) : 0) || 0);
        return originalUnit > saleUnit ? originalUnit : 0;
    },

    updateLineItemUnitPriceDisplay(row, item) {
        if (!row || !item || this.isBundleOfferItem(item) || row.classList.contains('cart-item--easybundle-parent')) return;

        const unitPrice = this.getItemUnitPrice(item);
        const unitMrp = this.getItemUnitMrp(item, row);
        if (unitPrice <= 0) return;

        row.dataset.lineUnitPrice = String(unitPrice);
        if (unitMrp > unitPrice) row.dataset.lineUnitMrp = String(unitMrp);

        const priceElement = row.querySelector('.cart-item__final-price') ||
            [...row.querySelectorAll('.cart-item__price-wrapper-inline .price, .cart-item__price-wrapper .price')]
                .find((element) => !element.closest('s') && !element.classList.contains('cart-item__old-price'));
        if (priceElement) priceElement.textContent = this.formatMoney(unitPrice);

        const mrpElement = row.querySelector('[data-line-item-mrp]');
        if (mrpElement && unitMrp > unitPrice) mrpElement.textContent = this.formatMrpMoney(unitMrp);

        const oldPriceElement = row.querySelector('.cart-item__old-price');
        if (oldPriceElement) {
            if (unitMrp > unitPrice) {
                oldPriceElement.textContent = this.formatMrpMoney(unitMrp);
                oldPriceElement.style.display = '';
            } else {
                oldPriceElement.textContent = '';
                oldPriceElement.style.display = 'none';
            }
        }
    },

    formatMrpMoney(amountInPaisa) {
        return this.formatMoney(amountInPaisa)
            .replace(/^Rs\.?\s*/i, 'MRP. ')
            .replace(/^₹\s*/, 'MRP. ');
    },

    calculateComplementaryDiscount(cart = this.cart) {
        if (!cart || !Array.isArray(cart.items)) return 0;

        const giftRow = document.querySelector('.dt-diwali-gift-row');
        const giftSaving = giftRow && !giftRow.classList.contains('is-preview')
            ? Number(giftRow.dataset.giftMrp) || 0 : 0;
        return cart.items.reduce((sum, item, index) => {
            if (item.properties?._dt_diwali_gift === '1') return sum;
            if (this.isBundleOfferItem(item)) return sum;

            const quantity = Number(item.quantity) || 1;
            const saleLine = Number(item.original_line_price || item.final_line_price || ((item.final_price || item.price || 0) * quantity) || 0);
            const compareAt = Number(item.selling_plan_allocation?.compare_at_price || item.variant?.compare_at_price || item.compare_at_price || 0);

            if (compareAt > 0) {
                return sum + Math.max(0, compareAt * quantity - saleLine);
            }

            const lineMrp = this.getRenderedLineMrp(item, index, saleLine, quantity);
            return sum + Math.max(0, lineMrp - saleLine);
        }, giftSaving);
    },

    getRenderedLineMrp(item, index, saleLine, quantity) {
        const row = this.getCartLineRow(item, index);
        if (!row) return 0;

        const dataUnitMrp = Number(row.dataset.lineUnitMrp || 0);
        if (dataUnitMrp > 0) return dataUnitMrp * quantity;

        const mrpElement = row.querySelector('[data-line-item-mrp], .cart-item__old-price');
        const renderedMrp = this.parseMoneyTextToCents(mrpElement?.textContent);
        if (!renderedMrp) return 0;
        if (renderedMrp > saleLine) return renderedMrp;

        const saleUnit = Number(item.final_price || item.price || Math.round(saleLine / Math.max(quantity, 1)) || 0);
        if (!saleUnit || renderedMrp <= saleUnit) return 0;

        let compareAtUnit = renderedMrp;
        let smallestGap = Math.abs(renderedMrp - saleUnit);

        for (let divisor = 2; divisor <= quantity; divisor += 1) {
            if (renderedMrp % divisor !== 0) continue;
            const candidate = Math.round(renderedMrp / divisor);
            if (candidate <= saleUnit) continue;
            const gap = Math.abs(candidate - saleUnit);
            if (gap < smallestGap) {
                compareAtUnit = candidate;
                smallestGap = gap;
            }
        }

        return compareAtUnit * quantity;
    },

    getCartLineRow(item, index) {
        let rows = [];

        if (item?.key) {
            const escapedKey = window.CSS && typeof window.CSS.escape === 'function'
                ? window.CSS.escape(item.key)
                : String(item.key).replace(/"/g, '\\"');
            rows = [...document.querySelectorAll(`[data-item-key="${escapedKey}"]`)];
            const row = this.getPreferredCartLineRow(rows);
            if (row) return row;
            rows = [...document.querySelectorAll('[data-merged-item-keys]')]
                .filter((mergedRow) => this.getMergedRowKeys(mergedRow).includes(item.key));
            const mergedRow = this.getPreferredCartLineRow(rows);
            if (mergedRow) return mergedRow;
        }

        rows = [...document.querySelectorAll(`#CartDrawer-Item-${index + 1}, #CartItem-${index + 1}`)];
        return this.getPreferredCartLineRow(rows);
    },

    getPreferredCartLineRow(rows = []) {
        if (!rows.length) return null;

        return rows.find((row) => {
            const style = getComputedStyle(row);
            return style.display !== 'none' && style.visibility !== 'hidden' && !!row.getClientRects().length;
        }) || rows[0];
    },

    getInfoIconMarkup() {
        return '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M9.3776 13.959H10.6276V9.16732H9.3776V13.959Z" fill="#B5B5B5"/><path d="M10.4801 7.54753C10.6105 7.4185 10.6757 7.25864 10.6757 7.06794C10.6757 6.87725 10.6112 6.71739 10.4822 6.58836C10.3532 6.45947 10.1933 6.39503 10.0026 6.39503C9.81191 6.39503 9.65205 6.45947 9.52302 6.58836C9.39399 6.71739 9.32948 6.87725 9.32948 7.06794C9.32948 7.25864 9.39469 7.4185 9.5251 7.54753C9.65538 7.67655 9.81455 7.74107 10.0026 7.74107C10.1907 7.74107 10.3498 7.67655 10.4801 7.54753Z" fill="#B5B5B5"/><path d="M10.0041 17.9173C8.90906 17.9173 7.87983 17.7095 6.91635 17.294C5.95288 16.8784 5.11483 16.3145 4.40219 15.6021C3.68955 14.8898 3.12531 14.052 2.70948 13.089C2.29378 12.1259 2.08594 11.097 2.08594 10.0021C2.08594 8.90711 2.29372 7.87787 2.70927 6.9144C3.12483 5.95093 3.68879 5.11287 4.40115 4.40023C5.11351 3.6876 5.95122 3.12336 6.91427 2.70753C7.87733 2.29183 8.90629 2.08398 10.0011 2.08398C11.0961 2.08398 12.1254 2.29176 13.0889 2.70732C14.0523 3.12287 14.8904 3.68683 15.603 4.39919C16.3157 5.11155 16.8799 5.94926 17.2957 6.91232C17.7114 7.87537 17.9193 8.90433 17.9193 9.99919C17.9193 11.0942 17.7115 12.1234 17.2959 13.0869C16.8804 14.0504 16.3164 14.8884 15.6041 15.6011C14.8917 16.3137 14.054 16.8779 13.0909 17.2938C12.1279 17.7095 11.0989 17.9173 10.0041 17.9173ZM10.0026 16.6673C11.8637 16.6673 13.4401 16.0215 14.7318 14.7298C16.0234 13.4382 16.6693 11.8618 16.6693 10.0007C16.6693 8.13954 16.0234 6.56315 14.7318 5.27148C13.4401 3.97982 11.8637 3.33398 10.0026 3.33398C8.14149 3.33398 6.5651 3.97982 5.27344 5.27148C3.98177 6.56315 3.33594 8.13954 3.33594 10.0007C3.33594 11.8618 3.98177 13.4382 5.27344 14.7298C6.5651 16.0215 8.14149 16.6673 10.0026 16.6673Z" fill="#B5B5B5"/></svg>';
    },

    ensureDiscountBreakdownRows({ complementaryDiscount = 0, tierDiscount = 0, bundlePriceAdjustment = 0 } = {}) {
        const contents = document.querySelectorAll('.cart-drawer__price-breakdown-content');

        contents.forEach((content, index) => {
            const totalRow = content.querySelector('.price-breakdown__row--total');
            if (!totalRow) return;

            if (complementaryDiscount > 0 && !content.querySelector('[data-discount-row]')) {
                totalRow.insertAdjacentHTML('beforebegin', `
                    <div class="price-breakdown__row" data-discount-row>
                      <span class="price-breakdown__label">
                        Limited time discount
                        <span class="price-breakdown__badge" style="background-color: rgba(76, 175, 80, 0.1); color: rgb(76, 175, 80); padding: 2px 8px; border-radius: 0; font-size: calc(var(--font-body-scale) * 10px); font-weight: 500; margin-left: 2px; display: inline-flex; align-items: center; gap: 4px;"><span style="font-weight: bold;">%</span> Applied</span>
                      </span>
                      <span class="price-breakdown__value" style="color: rgb(76, 175, 80);" data-complementary-discount></span>
                    </div>
                `);
            }

            if (tierDiscount > 0 && !content.querySelector('[data-tier-row]')) {
                const tierEligibleValue = this.getEligibleCartValue(this.cart);
                const tierPct = tierEligibleValue > 0 ? Math.round((tierDiscount / tierEligibleValue) * 100) : 0;
                const infoId = `tier-info-dynamic-${index}`;
                totalRow.insertAdjacentHTML('beforebegin', `
                    <div class="price-breakdown__row" data-tier-row>
                      <span class="price-breakdown__label">
                        Buy More Save More
                        <button type="button" class="price-breakdown__info-icon" aria-label="Buy More Save More information" aria-expanded="false" aria-controls="${infoId}">${this.getInfoIconMarkup()}</button>
                        <span class="price-breakdown__badge" style="background-color: rgba(76, 175, 80, 0.1); color: rgb(76, 175, 80); padding: 2px 8px; border-radius: 0; font-size: calc(var(--font-body-scale) * 10px); font-weight: 500; margin-left: 2px; display: inline-flex; align-items: center; gap: 4px;">${tierPct}%</span>
                        <span class="price-breakdown__badge" style="background-color: rgba(76, 175, 80, 0.1); color: rgb(76, 175, 80); padding: 2px 8px; border-radius: 0; font-size: calc(var(--font-body-scale) * 10px); font-weight: 500; margin-left: 2px; display: inline-flex; align-items: center; gap: 4px;">Applied at Checkout</span>
                      </span>
                      <span class="price-breakdown__value" style="color: rgb(76, 175, 80);" data-tier-discount></span>
                    </div>
                    <div class="price-breakdown__info-section" id="${infoId}" role="region" hidden>
                      <div class="price-breakdown__info-section-content">Bundles Offers are not counted in Buy More Save More. This discount is calculated only on normal product cart value.</div>
                    </div>
                `);
            }

            if (bundlePriceAdjustment > 0 && !content.querySelector('[data-bundle-discount-row]')) {
                totalRow.insertAdjacentHTML('beforebegin', `
                    <div class="price-breakdown__row" data-bundle-discount-row>
                      <span class="price-breakdown__label">Bundle Discount</span>
                      <span class="price-breakdown__value" style="color: rgb(76, 175, 80);" data-bundle-discount></span>
                    </div>
                `);
            }
        });
    },





    async autoManageGifts() {
        // Gifts management removed
    },

    updateProgressMessage() {
        // Progress message removed
    },


    updateEmptyState() {
        if (!this.cart) return;

        const isEmpty = this.cart.item_count === 0 && !this.hasPendingAddSurface();
        const drawer = document.querySelector('cart-drawer');
        const drawerInner = document.querySelector('.drawer__inner');
        const emptyState = document.querySelector('.drawer__inner-empty');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const footer = document.querySelector('.drawer__footer');

        if (drawer) {
            drawer.classList.toggle('is-empty', isEmpty);
        }
        if (drawerInner) {
            drawerInner.classList.toggle('is-empty', isEmpty);
        }
        if (cartDrawerItems) {
            cartDrawerItems.classList.toggle('is-empty', isEmpty);
        }
        if (emptyState) {
            emptyState.hidden = !isEmpty;
        }
        if (footer) {
            footer.hidden = isEmpty;
        }
        if (isEmpty) {
            removeCartDrawerMerchandising();
        }
    },

    // ============================================
    // CART DRAWER CONTROL
    // ============================================
    openDrawer(options = {}) {
        if (
            isEasyBundlePage() &&
            window.__dtEasyBundleCartDrawerOpenedOnce &&
            window.__dtEasyBundleCartDrawerDismissed &&
            !options.force
        ) {
            return;
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
            drawer.open(null, options);
        } else if (drawer) {
            drawer.classList.add('active', 'animate');
        }
    },

    applyOptimisticQuantityUpdate(lineIndex, quantity) {
        if (!this.cart?.items?.[lineIndex - 1]) return;
        const item = this.cart.items[lineIndex - 1];
        this.applyOptimisticQuantityUpdateByKey(item.key, quantity);
    },

    applyOptimisticQuantityUpdateByKey(itemKey, quantity) {
        if (!itemKey || !this.cart?.items) return;
        const itemIndex = this.cart.items.findIndex((cartItem) => cartItem.key === itemKey);
        if (itemIndex < 0) return;
        const item = this.cart.items[itemIndex];
        if (!item) return;

        const previousQuantity = Number(item.quantity) || 0;
        const nextQuantity = Math.max(0, Number(quantity) || 0);
        const finalUnitPrice = Number(item.final_price || (previousQuantity ? item.final_line_price / previousQuantity : item.price) || 0);
        const originalUnitPrice = Number(item.original_price || (previousQuantity ? item.original_line_price / previousQuantity : finalUnitPrice) || finalUnitPrice);
        const quantityDelta = nextQuantity - previousQuantity;
        const finalDelta = finalUnitPrice * quantityDelta;

        if (nextQuantity === 0) {
            this.cart.items.splice(itemIndex, 1);
        } else {
            item.quantity = nextQuantity;
            item.final_line_price = finalUnitPrice * nextQuantity;
            item.original_line_price = originalUnitPrice * nextQuantity;
        }
        this.cart.item_count = Math.max(0, (Number(this.cart.item_count) || 0) + quantityDelta);
        this.cart.items_subtotal_price = Math.max(0, (Number(this.cart.items_subtotal_price) || 0) + finalDelta);
        this.cart.total_price = Math.max(0, (Number(this.cart.total_price) || 0) + finalDelta);
    },

    closeDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.close === 'function') {
            drawer.close();
        } else if (drawer) {
            drawer.classList.remove('active');
        }
    },

    // ============================================
    // HELPERS
    // ============================================
    applyPendingCartUpdates() {
        this.pendingRemovalKeys.forEach((itemKey) => this.applyOptimisticRemoveByKey(itemKey));
        this.pendingQuantityUpdates.forEach((update, itemKey) => {
            if (!this.pendingRemovalKeys.has(itemKey)) this.applyOptimisticQuantityUpdateByKey(itemKey, update.quantity);
        });
    },

    async refreshCart() {
        if (this.pendingQuantityUpdates.size || this.pendingRemovalKeys.size) return;
        const mutationVersion = this.mutationVersion;
        try {
            const doFetch = () => fetch('/cart.js');
            const withRetry = window.cartAddFetchWithRetry
                ? (fn) => window.cartAddFetchWithRetry(fn)
                : (fn) => fn();
            const response = await withRetry(doFetch);
            if (response.ok) {
                const syncedCart = await response.json();
                if (!this.isCurrentMutation(mutationVersion)) return;
                this.cart = syncedCart;
                this.applyPendingCartUpdates();
            }
        } catch (error) {
            console.error('[CartController] Refresh failed:', error);
        }
    },

    getValidCartDrawerRoot(doc) {
        const drawerRoot = doc?.querySelector?.('#CartDrawer');
        if (!drawerRoot) return null;

        const hasDrawerShell = drawerRoot.querySelector('.drawer__inner');
        const hasDrawerScrollArea = drawerRoot.querySelector('.cart_overflow_inner');
        const hasDrawerItems = drawerRoot.querySelector('cart-drawer-items');
        const hasDrawerForm = drawerRoot.querySelector('#CartDrawer-Form');

        return hasDrawerShell && hasDrawerScrollArea && hasDrawerItems && hasDrawerForm
            ? drawerRoot
            : null;
    },

    removePendingRemovalRows(root) {
        if (!root || !this.pendingRemovalKeys?.size) return;
        this.pendingRemovalKeys.forEach((itemKey) => {
            if (!itemKey) return;
            const escapedKey = window.CSS && typeof window.CSS.escape === 'function'
                ? window.CSS.escape(itemKey)
                : String(itemKey).replace(/"/g, '\\"');
            root.querySelectorAll(`[data-item-key="${escapedKey}"], cart-remove-button[data-item-key="${escapedKey}"]`).forEach((element) => {
                const row = element.closest?.('.cart-item') || element;
                row.remove();
            });
        });
    },

    applyPendingQuantityRows(root) {
        if (!root || !this.pendingQuantityUpdates?.size) return;
        this.pendingQuantityUpdates.forEach((update, itemKey) => {
            if (!itemKey) return;
            const escapedKey = window.CSS && typeof window.CSS.escape === 'function'
                ? window.CSS.escape(itemKey)
                : String(itemKey).replace(/"/g, '\\"');
            root.querySelectorAll(`[data-item-key="${escapedKey}"]`).forEach((row) => {
                const quantity = Math.max(0, parseInt(update.quantity, 10) || 0);
                if (quantity === 0) {
                    row.remove();
                    return;
                }
                const input = row.querySelector('.quantity__input');
                if (input) {
                    input.value = this.getRowDisplayQuantity(row, quantity);
                    input.dataset.cartQuantity = quantity;
                }
                const item = this.cart?.items?.find((cartItem) => cartItem.key === itemKey);
                if (item) this.updateLineItemUnitPriceDisplay(row, item);
            });
        });
    },

    calculateTotalDiscount() {
        if (!this.cart) return 0;

        const complementary = this.calculateComplementaryDiscount(this.cart);
        const coupon = this.cart.total_discount || 0;
        const bundlePricingTotals = this.getBundlePricingTotals(this.cart);
        const existingBundleDiscount = Math.max(0, bundlePricingTotals.originalBundleTotal - bundlePricingTotals.currentBundleTotal);
        const cartDiscount = Math.max(0, coupon - existingBundleDiscount);
        return complementary + cartDiscount + Math.max(0, bundlePricingTotals.originalBundleTotal - bundlePricingTotals.fixedBundleTotal);
    },

    formatMoney(amountInPaisa) {
        const price = amountInPaisa / 100;
        const currencyEnabled = document.documentElement.dataset.currencyCodeEnabled === 'true';
        if (currencyEnabled && this.cart && this.cart.currency) {
            return new Intl.NumberFormat(document.documentElement.lang || 'en', {
                style: 'currency',
                currency: this.cart.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(price);
        }
        return `₹${price.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    },

    parseMoneyTextToCents(text) {
        if (!text) return 0;
        let normalized = String(text).replace(/[^0-9.]/g, '');
        // "Rs. 1,500" -> ".1500" (dot from "Rs." kept); strip leading dot to get "1500"
        if (normalized.startsWith('.')) normalized = normalized.slice(1);
        if (!normalized) return 0;
        const value = parseFloat(normalized);
        if (Number.isNaN(value)) return 0;
        return Math.round(value * 100);
    },

    showLoading() {
        // Show loading state on drawer
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
            drawer.classList.add('is-loading');
        }

        // Show loading on line items
        document.querySelectorAll('.loading__spinner').forEach(spinner => {
            // Don't show spinners - rely on subtle opacity changes
        });
    },

    hideLoading() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
            drawer.classList.remove('is-loading');
        }
    },

    showError(message) {
        const errorContainer = document.querySelector('#CartDrawer-CartErrors, #cart-errors');
        if (errorContainer) {
            errorContainer.textContent = message;
            setTimeout(() => {
                errorContainer.textContent = '';
            }, 5000);
        }
    },

    clearError() {
        document.querySelectorAll('#CartDrawer-CartErrors, #cart-errors').forEach((errorContainer) => {
            errorContainer.textContent = '';
        });
    },

    async waitForProcessing() {
        return new Promise(resolve => {
            const check = () => {
                if (!this.isProcessing) {
                    resolve();
                } else {
                    setTimeout(check, 50);
                }
            };
            check();
        });
    }
};
CartController.__dtScriptLoaded = true;
CartController.__dtInitialized = false;
window.CartController = CartController;

// ============================================
// GLOBAL HELPER FUNCTION
// ============================================
// Usage from anywhere: addToCart(variantId, quantity)
window.addToCart = async function (variantId, quantity = 1, properties = {}) {
    return CartController.add(variantId, quantity, properties);
};

// ============================================
// INITIALIZE
// ============================================
function initializeCartController() {
    if (CartController.__dtInitialized) {
        applyEasyBundleCartRowMeta();
        window.dispatchEvent(new CustomEvent('theme:cart-ready'));
        return;
    }

    CartController.__dtInitialized = true;

    try {
        Promise.resolve(CartController.init()).finally(() => {
            applyEasyBundleCartRowMeta();
            window.dispatchEvent(new CustomEvent('theme:cart-ready'));
        });
    } catch (error) {
        console.error('[CartController] Init failed:', error);
        applyEasyBundleCartRowMeta();
        window.dispatchEvent(new CustomEvent('theme:cart-ready'));
    }
}

document.addEventListener('cart:updated', applyEasyBundleCartRowMeta);

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartController, { once: true });
} else {
    initializeCartController();
}
})();
